import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "en" | "ar";

const en = {
  appName: "PeopleCore",
  tagline: "One HR core. Tick the modules you need.",
  switchLang: "العربية",
  loading: "Loading…",
  // sign in
  signInTitle: "Sign in to PeopleCore",
  signUpTitle: "Create your account",
  email: "Work email",
  password: "Password",
  passwordHint: "At least 8 characters.",
  fullName: "Full name",
  signIn: "Sign in",
  signUp: "Create account",
  haveAccount: "Already have an account?",
  noAccount: "New to PeopleCore?",
  switchToSignIn: "Sign in",
  switchToSignUp: "Create an account",
  checkEmail: "Check your email for a confirmation link, then sign in.",
  signOut: "Sign out",
  // setup
  setupTitle: "Set up your company",
  stepCompany: "Company",
  stepPreset: "Starting point",
  stepModules: "Modules",
  companyName: "Company name (English)",
  companyNameAr: "Company name (Arabic)",
  optional: "Optional",
  country: "Country",
  defaultLanguage: "Default language",
  english: "English",
  arabic: "Arabic",
  next: "Next",
  back: "Back",
  presetHelp: "Pick the closest fit. You can change any module on the next step, or later.",
  modulesHelp: "Tick the modules this company needs. Modules they depend on are ticked for you.",
  requiredBy: "Required by",
  alwaysOn: "Always on",
  arrives: "Arrives in",
  createCompany: "Create company",
  creating: "Creating your company…",
  nameRequired: "Enter the company's name.",
  // shell
  navHome: "Home",
  navOrganisation: "Organisation",
  navMembers: "Members and roles",
  navModules: "Modules",
  navWorkspace: "Workspace",
  navYourModules: "Your modules",
  greeting: "Welcome",
  homeIntro: "Your company is set up. Here is what is switched on.",
  modulesOn: "modules on",
  yourRole: "Your role",
  comingSoonTitle: "This module is on its way",
  comingSoonBody: "It is switched on for your company and will appear here when its milestone is built.",
  // organisation
  departments: "Departments",
  addDepartment: "Add department",
  departmentName: "Department name (English)",
  departmentNameAr: "Department name (Arabic)",
  parentDepartment: "Part of",
  none: "None",
  save: "Save",
  cancel: "Cancel",
  noDepartments: "No departments yet. Add your first one.",
  branches: "Branches",
  headOffice: "Head office",
  // members
  members: "People with access",
  role: "Role",
  status: "Status",
  active: "Active",
  inactive: "Inactive",
  // modules page
  modulesTitle: "Modules",
  modulesPageHelp: "Switch modules on or off for the whole company. Switching a module off hides it; its data is kept.",
  saveChanges: "Save changes",
  saved: "Saved",
  noPermission: "You don't have permission to change this.",
  errorGeneric: "Something went wrong. Try again.",
  // core HR (M3)
  navEmployees: "Employees",
  navDocuments: "Hiring documents",
  employees: "Employees",
  searchEmployees: "Search by name, number or job",
  allStatuses: "All statuses",
  addEmployee: "Add employee",
  importEmployees: "Import",
  noEmployees: "No employees yet. Add your first one or import a file.",
  employeeNumber: "Employee no.",
  autoNumber: "Leave empty to number automatically",
  jobTitle: "Job title",
  jobTitleAr: "Job title (Arabic)",
  department: "Department",
  branch: "Branch",
  manager: "Manager",
  hireDate: "Hire date",
  statusActive: "Active",
  statusProbation: "In probation",
  statusStarting: "Starting soon",
  statusTerminated: "Left",
  firstName: "First name",
  lastName: "Last name",
  firstNameAr: "First name (Arabic)",
  lastNameAr: "Last name (Arabic)",
  gender: "Gender",
  female: "Female",
  male: "Male",
  dateOfBirth: "Date of birth",
  nationality: "Nationality",
  egyptian: "Egyptian",
  otherNationality: "Other (ISO code, e.g. SA)",
  nationalId: "National ID",
  nationalIdHint: "14 digits",
  mobile: "Mobile",
  workEmail: "Work email",
  personalEmail: "Personal email",
  contractType: "Contract type",
  contractEnd: "Contract end date",
  probationEnd: "Probation end date",
  probationHint: "At most 3 months after the hire date",
  grade: "Grade",
  fte: "FTE",
  hireSource: "Hire source",
  sectionPerson: "Personal details",
  sectionJob: "Job",
  sectionContract: "Contract",
  backToList: "All employees",
  jobHistory: "Job history",
  from: "From",
  to: "To",
  change: "Change",
  current: "Current",
  documents: "Documents",
  docStatus_pending: "Pending",
  docStatus_overdue: "Overdue",
  docStatus_received: "Received",
  docStatus_expiring: "Expiring soon",
  docStatus_expired: "Expired",
  docStatus_waived: "Waived",
  docStatus_rejected: "Rejected",
  dueDate: "Due",
  expiryDate: "Expiry date",
  receivedOn: "Received on",
  markReceived: "Mark received",
  waive: "Waive",
  undo: "Undo",
  required: "Required",
  showAll: "Show all",
  needsAttention: "Needs attention",
  allDone: "Nothing needs attention. Every required document is in.",
  overdueCount: "overdue",
  pendingCount: "pending",
  expiringCount: "expiring",
  importTitle: "Import employees",
  importStep1: "Download the template and fill it in Excel, one employee per row.",
  downloadTemplate: "Download template (CSV)",
  importStep2: "In Excel choose File → Save As → CSV UTF-8, then pick the file here.",
  chooseFile: "Choose CSV file",
  rowsFound: "rows found",
  runImport: "Import rows",
  importing: "Importing…",
  imported: "imported",
  failed: "failed",
  row: "Row",
  error: "Error",
  close: "Close",
  terminated: "Left on",
  sampleTitle: "Try PeopleCore with sample data",
  sampleBody: "Load a sample company: 42 people in 7 departments and 2 branches, with managers, leavers and a rehire, leave in every state, 45 days of attendance, letters, policies and requests waiting for you. You are linked to the sample HR Manager so you can try the staff screens too. Everything can be removed in one step.",
  sampleLoad: "Load sample data",
  sampleLoaded: "sample employees added.",
  sampleOnTitle: "Sample data is loaded",
  sampleOnBody: "Explore every screen with the sample company. Reset it to start fresh with today's dates, or remove it before you import your real employees; your own records are never touched.",
  sampleRemove: "Remove sample data",
  sampleRemoveConfirm: "Remove all sample employees?",
  sampleRemoved: "sample employees removed.",
  // employee actions (M3c)
  editDetails: "Edit details",
  changeJob: "Change job",
  changeManager: "Change manager",
  endEmployment: "End employment",
  effectiveFrom: "Effective from",
  reason: "Reason",
  reason_promotion: "Promotion",
  reason_transfer: "Transfer",
  reason_role_change: "Role change",
  reason_contract_change: "Contract change",
  reason_correction: "Correction",
  reason_hire: "Hire",
  reason_rehire: "Rehire",
  keepCurrent: "Keep current",
  noManager: "No manager",
  lastDay: "Last working day",
  leavingReason: "Leaving reason",
  regrettable: "Regrettable leaver (we wanted to keep them)",
  rehireEligible: "Eligible for rehire",
  endEmploymentHelp: "Their job, contract and reporting lines end on the last day. People who reported to them will need a new manager.",
  confirmEnd: "End employment",
  done: "Saved",
  // settings (M3c)
  navSettings: "Settings",
  customFields: "Custom fields",
  customFieldsHelp: "Add your own fields to employee records. They appear on every profile.",
  fieldLabel: "Label (English)",
  fieldLabelAr: "Label (Arabic)",
  fieldType: "Type",
  type_text: "Text",
  type_number: "Number",
  type_date: "Date",
  type_boolean: "Yes / no",
  type_select: "List of options",
  fieldOptions: "Options, separated by commas",
  appliesToRecord: "Belongs to",
  entity_person: "The person",
  entity_employment: "The employment",
  addField: "Add field",
  noFields: "No custom fields yet.",
  activeLabel: "In use",
  hiringDocsList: "Hiring documents list",
  hiringDocsHelp: "Choose which documents you ask new hires for. Switch off any you don't need.",
  documentName: "Document name (English)",
  documentNameAr: "Document name (Arabic)",
  appliesTo: "Asked of",
  applies_all: "Everyone",
  applies_male: "Men",
  applies_female: "Women",
  applies_egyptian: "Egyptians",
  applies_non_egyptian: "Non-Egyptians",
  hasExpiry: "Has an expiry date",
  addDocument: "Add document",
  linkedEmployee: "Employee record",
  notLinked: "Not linked",
  linkHelp: "Link a login to an employee record so that person can see their own file.",
  yes: "Yes",
  no: "No",
  customDetails: "More details",
  navMyFile: "My file",
  // leave (M4)
  navLeave: "Leave",
  navMyLeave: "My leave",
  tabRequests: "Requests",
  tabBalances: "Balances",
  tabHolidays: "Holidays",
  newRequest: "New leave request",
  employee: "Employee",
  leaveType: "Leave type",
  startDate: "First day",
  endDate: "Last day",
  days: "Days",
  note: "Note",
  approveNow: "Approve now",
  submitRequest: "Send request",
  approve: "Approve",
  reject: "Reject",
  cancelRequest: "Cancel request",
  leaveStatus_pending: "Pending",
  leaveStatus_approved: "Approved",
  leaveStatus_rejected: "Rejected",
  leaveStatus_cancelled: "Cancelled",
  pendingApproval: "Waiting for a decision",
  noPending: "No requests are waiting.",
  allRequests: "All requests",
  noRequests: "No leave requests yet.",
  year: "Year",
  entitlement: "Entitlement",
  carriedOver: "Carried over",
  adjusted: "Adjusted",
  taken: "Taken",
  pendingDays: "Pending",
  remaining: "Remaining",
  loadBalances: "Show balances",
  addAdjustment: "Add opening balance or correction",
  adjustmentDays: "Days (use minus to deduct)",
  adjustmentReason: "Reason",
  publicHolidays: "Public holidays",
  companyHolidays: "Company holidays",
  addHoliday: "Add company holiday",
  holidayName: "Holiday name",
  date: "Date",
  tentative: "Date to be confirmed",
  noHolidays: "No company holidays added.",
  leaveRules: "Rules are checked when you send: balance, working days, weekends and holidays.",
  myBalances: "My balances",
  myRequests: "My requests",
  remove: "Remove",
  daysUnit: "days",
  // attendance (M4c)
  navAttendance: "Attendance",
  tabDay: "Day",
  tabSummary: "Monthly summary",
  tabImport: "Import",
  att_present: "Present",
  att_late: "Late",
  att_on_leave: "On leave",
  att_absent: "Absent",
  att_day_off: "Day off",
  att_upcoming: "Upcoming",
  checkIn: "In",
  checkOut: "Out",
  minutesLate: "Min late",
  month: "Month",
  workingDays: "Working days",
  presentDays: "Present",
  lateDays: "Late",
  lateMinutes: "Late (min)",
  leaveDays: "Leave",
  absentDays: "Absent",
  missingOut: "No check-out",
  attImportHelp1: "Daily file: one row per person per day with employee_number, date, check_in, check_out.",
  attImportHelp2: "Fingerprint export: one row per punch with employee_number and punch (date and time). The first punch of the day is the check-in and the last is the check-out.",
  dailyTemplate: "Daily template",
  punchTemplate: "Punch template",
  edit: "Edit",
  noEmployeesDay: "No employees on this day.",
  // workflows (M5)
  navApprovals: "Approvals",
  navTeam: "My team",
  waitingForYou: "waiting for your decision",
  openApprovals: "Open approvals",
  needsAttentionTitle: "Needs attention",
  alert_contract_end: "Contract ends",
  alert_probation_end: "Probation ends",
  alert_document_expiring: "Document expires",
  alert_document_expired: "Document expired",
  alert_documents_overdue: "Hiring documents overdue",
  alert_birthday: "Birthday",
  inDays: "in {n} days",
  today: "today",
  daysAgo: "{n} days ago",
  noApprovals: "Nothing is waiting for you.",
  stage_manager: "Manager step",
  stage_hr: "HR step",
  decisionNote: "Note (optional)",
  delegateTitle: "Away? Hand over your approvals",
  delegateTo: "Cover person",
  delegateSave: "Hand over",
  delegated: "Approvals handed over.",
  noTeam: "Nobody reports to you yet.",
  inviteTitle: "Invite people",
  inviteHelp: "They sign up at the app address with this email and join your company automatically.",
  inviteEmail: "Email",
  sendInvite: "Create invitation",
  pendingInvites: "Open invitations",
  copyMessage: "Copy message",
  copied: "Copied",
  revoke: "Revoke",
  inviteMessage: "You are invited to {company} on PeopleCore. Open {link}, create an account with {email}, and you will join automatically.",
  joinTitle: "You have been invited",
  joinBody: "Join your company on PeopleCore.",
  join: "Join",
  orSetUp: "Or set up a new company instead",
  approvalChain: "Leave approval",
  chain_manager_hr: "Manager, then HR",
  chain_manager: "Manager only",
  chain_hr: "HR only",
  chain_none: "No approval (approved on request)",
  approvalChainHelp: "If someone has no manager with a login, their request goes straight to HR.",
  // letters and policies (M6)
  navLetters: "Letters",
  navPolicies: "Policies",
  tabIssue: "Issue",
  tabLetterRequests: "Requests",
  tabTemplates: "Templates",
  tabHistory: "History",
  letterTemplate: "Letter",
  fillBlanks: "Fill in the blanks",
  preview: "Preview",
  printAndIssue: "Issue and print",
  printAgain: "Print",
  issued: "Issued",
  reference: "Reference",
  purpose: "Purpose",
  requestLetter: "Request a letter",
  requestSent: "Request sent to HR.",
  myLetters: "My letters",
  decline: "Decline",
  noLetterRequests: "No letter requests waiting.",
  noLetters: "No letters issued yet.",
  bodyEn: "Text (English)",
  bodyAr: "Text (Arabic)",
  selfService: "Staff can request it",
  placeholdersHelp: "Words in braces are filled in automatically, e.g. {employee_name}, {job_title}, {hire_date}. Any other {word} becomes a blank to fill in when issuing.",
  signatoryName: "Signed by (name)",
  signatoryTitle: "Signed by (title)",
  referencePrefix: "Reference prefix",
  letterSettings: "Letter settings",
  publishPolicy: "Publish a policy",
  newVersion: "Publish new version",
  policyTitle: "Title (English)",
  policyTitleAr: "Title (Arabic)",
  policyBody: "Policy text (English)",
  policyBodyAr: "Policy text (Arabic)",
  requiresAck: "Employees must acknowledge it",
  publish: "Publish",
  acknowledged: "Acknowledged",
  acknowledge: "I have read and accept this policy",
  toAcknowledge: "To acknowledge",
  version: "Version",
  progress: "Acknowledged",
  noPolicies: "No policies published yet.",
  readPolicy: "Read",
  linkFirst: "Ask HR to link your login to your employee record first.",
  nameEn: "Name (English)",
  nameAr: "Name (Arabic)",
  pending: "Pending",
  navDashboard: "Dashboard",
  navMyLetters: "My letters",
  dashHeadcount: "Headcount",
  dashVsYearAgo: "vs a year ago",
  dashHires12: "Hires, last 12 months",
  dashLast90: "in the last 90 days",
  dashTurnover: "Voluntary turnover, 12 months",
  dashAllLeavers: "All leavers",
  dashEarlyLeavers: "Left within their first year",
  dashEarlyHint: "last 12 months",
  dashTenure: "Average tenure",
  dashYears: "years",
  dashAvgAge: "Average age",
  dashProbation: "On probation",
  dashStartingSoon: "starting soon",
  dashOnLeave: "On leave today",
  dashPendingLeave: "requests waiting",
  dashAttendance: "Attendance this month",
  dashLate: "Late",
  dashDocsOverdue: "Missing hiring documents",
  dashDocsHint: "employees with overdue documents",
  dashLt1: "Under 1 year",
  dash1to3: "1–3 years",
  dash3to5: "3–5 years",
  dash5to10: "5–10 years",
  dash10plus: "10+ years",
  dashSampleNote: "{n} of these people are sample data. Remove the sample data from Home once you add your real team.",
  dashHeadcountTrend: "Headcount, last 12 months",
  dashShowTable: "Show as table",
  dashShowChart: "Show as chart",
  dashHiresLeavers: "Hires and leavers by month",
  dashHires: "Hires",
  dashLeavers: "Leavers",
  dashVoluntary: "voluntary",
  dashByDepartment: "By department",
  dashByTenure: "By length of service",
  dashByAge: "By age",
  dashByGender: "By gender",
  dashByContract: "By contract",
  dashByNationality: "By nationality",
  dashEgyptian: "Egyptian",
  dashOther: "Other nationalities",
  dashUnknown: "Not recorded",
  dashMonth: "Month",
  dashPowerBi: "Power BI",
  dashPowerBiText: "Build your own reports in Power BI from the same numbers. The guide shows how to create a read-only login and connect.",
  dashPowerBiGuide: "Open the Power BI guide",
  dashAsOf: "Figures as of",
  navMe: "Me",
  navAlerts: "Alerts",
  navMyRequests: "My requests",
  dates: "Dates",
  apprWaiting: "Waiting for you",
  apprCover: "Cover",
  apprLeave: "Leave requests",
  apprDetails: "Changes to personal details",
  apprLetters: "Letter requests",
  apprOpenLetters: "Open letters",
  apprNotOwn: "Another HR colleague must approve changes to your own record",
  apprHistoryTitle: "Your decisions, last 90 days",
  apprNoHistory: "You haven't decided on anything in the last 90 days.",
  apprYourDecision: "Your decision",
  apprDecidedOn: "Decided on",
  apprPassedOn: "Approved, sent to HR",
  apprCoverList: "Current and upcoming cover",
  apprNoCover: "No cover arranged.",
  apprCoveredBy: "Your approvals are covered by",
  apprCoveringFor: "You are covering approvals for",
  marital_single: "Single",
  marital_married: "Married",
  marital_divorced: "Divorced",
  marital_widowed: "Widowed",
  pfAddress: "Address",
  pfEmergencyName: "Emergency contact",
  pfEmergencyPhone: "Emergency contact phone",
  pfMarital: "Marital status",
  pfEducation: "Education",
  myDetails: "My personal details",
  requestChange: "Request a change",
  changeWaiting: "Waiting for HR:",
  detailsHelp: "Changes are checked by HR before they appear on your record.",
  noteForHr: "Note for HR (optional)",
  sendToHr: "Send to HR",
  myRequestsAll: "Everything I've asked for",
  detailsChange: "Personal details",
  noRequestsYet: "You haven't sent any requests yet.",
  letterStatus_pending: "Waiting",
  letterStatus_issued: "Issued",
  letterStatus_declined: "Declined",
  forYou: "For you",
  nothingForYou: "Nothing needs your attention.",
  rem_policy_ack: "Please acknowledge",
  rem_document_expiring: "Your document expires",
  rem_document_expired: "Your document expired",
  rem_leave_approved: "Leave approved",
  rem_leave_rejected: "Leave not approved",
  rem_letter_ready: "Letter ready",
  rem_letter_declined: "Letter declined",
  rem_profile_approved: "Details updated",
  rem_profile_rejected: "Details change declined",
  rem_probation_end: "Your probation ends",
  remGo_policies: "Policies",
  remGo_myleave: "My leave",
  remGo_myletters: "My letters",
  remGo_myrequests: "My requests",
  alertsPeople: "Coming up for your people",
  alertsFilter: "Filter alerts",
  alertsAll: "All",
  alertsNone: "Nothing coming up.",
  alertsDocsCount: "documents",
  alertsSettings: "Reminder settings",
  alertsContractDays: "Contract ends: days ahead",
  alertsProbationDays: "Probation ends: days ahead",
  alertsDocumentDays: "Document expiry: days ahead",
  alertsBirthdays: "Show birthdays (next 7 days)",
  alertsInAppOnly: "Alerts show in the app for now. Email reminders come once the company email sender is set up.",
  alertsSeeAll: "See all {n} alerts",
  sampleReset: "Reset sample data",
  dashLast12: "Last 12 months",
  dashYtd: "Year to date",
  dashQuarter: "Quarter",
  dashMonthly: "Month",
  dashPeriod: "Period",
  dashYear: "Year",
  dashFullYear: "full year",
  dashTurnoverP: "Voluntary turnover",
  dashPerYear: "a year",
  dashLeaveDays: "Leave days taken",
  dashAttendanceP: "Attendance",
  dashSinceStart: "since the period started",
  dashStartingSoonT: "Starting soon",
  dashInPeriod: "In this period",
  dashAtEnd: "At the end of the period",
  dashRightNow: "Right now",
  dashChosenPeriod: "Chosen period",
  dashMixAt: "Workforce on {date}",
  search: "Search",
  navPayroll: "Payroll",
  navMyPayslips: "My payslips",
  payRuns: "Payroll runs",
  paySetup: "Pay setup",
  payElements: "Pay elements",
  paySettings: "Payroll settings",
  payNewRun: "Prepare a payroll",
  payPrepare: "Prepare",
  payNoRuns: "No payroll yet. Pick a month and prepare it.",
  payPeople: "People",
  payNet: "Net pay",
  payGross: "Gross pay",
  payTax: "Income tax",
  payInsurance: "Social insurance",
  paySiTotal: "Social insurance (both shares)",
  payEmployerCost: "Employer cost",
  payPayDate: "Pay date",
  payStatus_draft: "Draft",
  payStatus_calculated: "Calculated",
  payStatus_approved: "Approved",
  payStatus_paid: "Paid",
  payStale: "Adjustments changed after the last calculation. Recalculate before approving.",
  payApprovals: "Approvals: {n} of {m}",
  payCalculate: "Calculate",
  payRecalculate: "Recalculate",
  payCalculated: "Calculated.",
  payMarkPaid: "Mark as paid",
  payMarkedPaid: "Marked as paid.",
  payReopen: "Reopen",
  payDelete: "Delete this payroll",
  payDeleteConfirm: "Delete this payroll and its adjustments?",
  payAdjustments: "One-off items this month",
  payRemove: "Remove",
  payPayslips: "Payslips",
  payExportSummary: "Payroll sheet (CSV)",
  payExportBank: "Bank transfer file (CSV)",
  payExportInsurance: "Social insurance list (CSV)",
  payNoPaySet: "No pay set up for {n} people",
  payUnpaidDays: "unpaid days",
  payPartMonth: "part month",
  payPayslip: "Payslip",
  payRulesNote: "Calculated with Egyptian rules: social insurance 11% / 18.75% within the year's insurable wage limits, salary tax under Law 7/2024 after the 20,000 annual exemption, and the Martyrs' Fund at 0.05%. Have your accountant check the first payroll.",
  payAddAdjustment: "Add a bonus, commission or deduction",
  payElement: "Pay element",
  payAmount: "Amount (EGP)",
  payAdd: "Add",
  payEarning: "earning",
  payDeduction: "deduction",
  payAdjHelp: "Monthly pay comes from Pay setup; add one-off items here, then recalculate.",
  payBasic: "Basic salary",
  payAllowances: "Allowances",
  payInsured: "Insured",
  payNotSet: "Not set",
  payView: "View",
  payFrom: "Effective from",
  payInsuredLong: "Covered by social insurance",
  payTaxExempt: "Exempt from income tax",
  payInsuranceNo: "Insurance number",
  payInsuranceSalary: "Insurance salary (optional)",
  payAuto: "Worked out from pay",
  payMethod: "Paid by",
  payBank: "Bank transfer",
  payCash: "Cash",
  payWallet: "Mobile wallet",
  payBankName: "Bank",
  payAccount: "Account number or IBAN",
  payHistory: "Pay history",
  payKind: "Type",
  payTaxable: "Taxable",
  payInsurable: "Insurable",
  payMonthly: "Monthly",
  payOneOff: "One-off",
  payElementsHelp: "Monthly elements are set per employee in Pay setup. One-off elements are added to a payroll month.",
  payNewElement: "New pay element",
  payApprovalLevels: "Approval",
  payOneApprover: "One person approves",
  payTwoApprovers: "Two different people approve",
  payPayDay: "Usual pay day (1–28)",
  payUhi: "Deduct universal health insurance",
  payUhiHelp: "Only switch this on if the universal health insurance system has started in your governorate (1% employee, 3% employer).",
  payNoMyPayslips: "No payslips yet. They appear here once payroll is approved.",
  navOvertime: "Shifts & overtime",
  navLoans: "Loans & advances",
  otTab: "Overtime",
  shiftsTab: "Shifts",
  otFind: "Find overtime from attendance",
  otFound: "{n} overtime entries found.",
  otHelp: "Overtime is time after the shift ends on working days (split into day and after-22:00 night time), and all time worked on rest days and public holidays. Approved overtime goes into the next payroll.",
  otApprovedHours: "approved hours",
  ot_day: "Day overtime",
  ot_night: "Night overtime",
  ot_rest_day: "Rest day",
  ot_holiday: "Public holiday",
  otStatus_suggested: "To review",
  otStatus_approved: "Approved",
  otStatus_rejected: "Rejected",
  otPaid: "Paid in payroll",
  otSelectAll: "Select all",
  otSelect: "Select",
  otApprovedN: "approved.",
  otRejectedN: "rejected.",
  otNone: "No overtime here.",
  otKind: "Type",
  otHours: "Hours",
  otAdd: "Record overtime",
  otAdded: "Overtime recorded and approved.",
  otSent: "Overtime sent for approval.",
  otRatesNote: "Paid at the hourly wage (monthly pay ÷ 30 ÷ 8): day overtime +35%, night overtime +70%, rest days and public holidays with the legal extra.",
  shiftNone: "No shifts yet. Everyone works the company hours set in Settings.",
  shiftBreak: "Break",
  shiftHelp: "Lateness and overtime follow each person's shift; people without a shift follow the company hours.",
  shiftAssign: "Put people on a shift",
  shiftOne: "Shift",
  shiftCompanyHours: "Company hours",
  shiftAssignBtn: "Assign",
  shiftAssigned: "{n} people updated.",
  shiftNew: "New shift",
  shiftStart: "Starts",
  shiftEnd: "Ends",
  apprOvertime: "Overtime to approve",
  apprApproveAll: "Approve all",
  apprOpenOvertime: "Open overtime",
  apprMoreOvertime: "{n} more on the overtime page.",
  apprOpenLoans: "Open loans",
  loanActive: "Active loans and advances",
  loanOutstanding: "Still to recover",
  loanRequests: "Loan and advance requests",
  loanMonths: "months",
  loanFirstMonth: "First deduction",
  loanInstallments: "Monthly instalments",
  loanApproved: "Approved; instalments start in the chosen month.",
  loanRejected: "Request declined.",
  loanNone: "No active loans or advances.",
  loanRecovered: "Recovered",
  loanLeft: "Left",
  loanNext: "Next",
  loanDone: "All scheduled",
  loanLeaver: "Left the company",
  loanClose: "Close",
  loanCloseAsk: "Close this loan and drop the instalments not yet deducted? Add a note (e.g. settled in cash).",
  loanClosed: "Loan closed.",
  loanRule: "Loans and advances are interest-free, and no more than 10% of monthly pay can be deducted each month (Labour Law 14/2025, Article 113).",
  loanPast: "Declined and closed",
  loanNew: "Give a loan or advance",
  loanGiven: "Loan created and scheduled.",
  loanGive: "Create",
  loanEach: "Each instalment",
  loanKind_loan: "Loan",
  loanKind_advance: "Salary advance",
  loanStatus_requested: "Waiting",
  loanStatus_active: "Active",
  loanStatus_rejected: "Declined",
  loanStatus_cancelled: "Closed",
  myLoans: "My loans and advances",
  loanAsk: "Ask for a loan or advance",
  loanReason: "Reason",
  loanNoneMine: "No loans or advances.",
  apprExpenses: "Expense claims",
  apprOpenExpenses: "Open expenses",
  asCondition: "Condition",
  asDamaged: "Damaged",
  asGood: "Good",
  asHandOut: "Hand out",
  asHolder: "Held by",
  asIssued: "Handed out.",
  asItem: "Item",
  asLeavers: "{n} items are still with people who have left",
  asLost: "Lost",
  asNew: "Add an asset",
  asNone: "No assets here.",
  asReturned: "Taken back.",
  asSerial: "Serial number",
  asTag: "Tag",
  asTakeBack: "Take back",
  asValue: "Value (EGP)",
  asStatus_available: "Available",
  asStatus_assigned: "With someone",
  asStatus_repair: "In repair",
  asStatus_retired: "Retired",
  asStatus_lost: "Lost",
  asCat_laptop: "Laptop",
  asCat_phone: "Phone",
  asCat_sim: "SIM card",
  asCat_tablet: "Tablet",
  asCat_vehicle: "Vehicle",
  asCat_card: "Card",
  asCat_key: "Key",
  asCat_uniform: "Uniform",
  asCat_tools: "Tools",
  asCat_other: "Other",
  bnCompanyPays: "Company pays",
  bnDepFactor: "Each dependent adds (× cost)",
  bnDependents: "dependents",
  bnEmployeeMonthly: "Employee share a month",
  bnEmployeePays: "Employee pays",
  bnEmployerMonthly: "Company cost a month",
  bnEnd: "End",
  bnEnded: "Ended.",
  bnEnroll: "Enrol",
  bnEnrolled: "Enrolled.",
  bnHelp: "The employee share is deducted in payroll each month; dependents add to both shares.",
  bnMonth: "month",
  bnNew: "New benefit",
  bnNone: "No benefits yet.",
  bnProvider: "Provider",
  bnCat_medical: "Medical",
  bnCat_life: "Life insurance",
  bnCat_transport: "Transport",
  bnCat_phone: "Phone",
  bnCat_meal: "Meals",
  bnCat_wellbeing: "Wellbeing",
  bnCat_other: "Other",
  exClaim: "Claim an expense",
  exHelp: "The manager approves first, then Finance. Approved claims are refunded in the next payroll.",
  exHelpFin: "Your approval is final. Approved claims are refunded, tax-free, in the next payroll.",
  exNone: "No claims here.",
  exNoneMine: "No expense claims.",
  exPaid: "Refunded",
  exReceipt: "Receipt number",
  exReceiptHint: "e.g. invoice or receipt no.",
  exRefunded: "Refunded in payroll",
  exSent: "Claim sent.",
  exSubmit: "Submit",
  exToRefund: "Approved, to refund",
  exWaiting: "Waiting for approval",
  exWaitingShort: "Waiting",
  exWhat: "What for",
  exStatus_submitted: "Waiting for manager",
  exStatus_manager_approved: "Waiting for Finance",
  exStatus_approved: "Approved",
  exStatus_rejected: "Rejected",
  exStatus_cancelled: "Cancelled",
  exCat_travel: "Travel",
  exCat_transport: "Transport",
  exCat_meals: "Meals",
  exCat_accommodation: "Accommodation",
  exCat_supplies: "Supplies",
  exCat_phone: "Phone",
  exCat_training: "Training",
  exCat_other: "Other",
  myAssets: "Company items with me",
  myAssetsHelp: "Return these to HR on your last day.",
  myExpenses: "My expense claims",
  navAssets: "Assets",
  navExpenses: "Expenses",
  navOffboarding: "Offboarding",
  navRewards: "Rewards & benefits",
  rwApprove: "Approve and add to payroll",
  rwApproved: "Approved; the amounts are in that month's payroll.",
  rwCreate: "Create",
  rwDeleteAsk: "Delete this draft plan?",
  rwEveryone: "Everyone",
  rwFill: "Fill in",
  rwFillHow: "How to fill",
  rwFilled: "Filled in.",
  rwFixed: "Fixed amount",
  rwHelp: "Prepare a bonus, commission, award or profit share for a month, check the amounts, then approve to add them to that month's payroll.",
  rwMonthsOfBasic: "Months of basic salary",
  rwNew: "New plan",
  rwNone: "No plans yet.",
  rwPayMonth: "Paid in",
  rwPlans: "Bonus and profit-share plans",
  rwProfitNote: "Profit share is set as not taxable and not insurable by default; confirm with your accountant (Pay elements).",
  rwTabBenefits: "Benefits",
  rwTabPlans: "Bonuses and profit share",
  rwTitle: "Title",
  rwTitleExample: "e.g. Year-end bonus 2026",
  rwTotal: "Total",
  rwKey_bonus: "Bonus",
  rwKey_commission: "Commission",
  rwKey_award: "Award",
  rwKey_profit_share: "Profit share",
  rwStatus_draft: "Draft",
  rwStatus_approved: "In payroll",
  rwStatus_paid: "Paid",
  xCalculate: "Work out the settlement",
  xCalculated: "Settlement worked out; check and adjust the amounts.",
  xChecklist: "Checklist",
  xClose: "Close the exit",
  xClosed: "Exit closed.",
  xComments: "Comments",
  xCourtMin: "For a dismissal without a legitimate reason, courts award at least 2 months' wage per year ({amt}).",
  xDone: "Closed and withdrawn",
  xInPayroll: "The settlement is in the {m} payroll.",
  xInProgress: "Exits in progress",
  xInfo: "Monthly pay {pay} · daily wage {daily} · {years} years of service.",
  xInterview: "Exit interview",
  xLastDay: "Last day",
  xLeaveDays: "Unused leave days",
  xMainReason: "Main reason for leaving",
  xNet: "Settlement total",
  xNone: "Nobody is leaving right now.",
  xNoticeDate: "Notice given",
  xNoticeMonths: "Notice pay (months)",
  xRecommend: "Would recommend us as an employer",
  xResign: "Resign",
  xResignHelp: "Your resignation goes to HR. The law asks for three months' written notice.",
  xResignOpen: "Your exit is in progress; last day {d}.",
  xResignSend: "Send my resignation",
  xResignSent: "Your resignation was sent to HR.",
  xResignTitle: "Resignation",
  xRules: "Labour Law 14/2025: three months' written notice; all dues paid within 7 working days of the last day; a gratuity only for people outside social insurance.",
  xSettle: "End employment and send to payroll",
  xSettleAsk: "End the employment on the last day and add the settlement to that month's payroll?",
  xSettled: "Done: the settlement is in payroll.",
  xSettlement: "Final settlement",
  xSettlementHelp: "Salary for days worked in the last month is paid by that month's payroll. Outstanding loans and unreturned assets are taken from the settlement.",
  xStart: "Start an exit",
  xStartBtn: "Start",
  xStillHolds: "Still holding company items",
  xUninsured: "Not covered by social insurance: a gratuity applies (Art. 172).",
  xWithdraw: "Withdraw",
  xWithdrawAsk: "Withdraw this exit (e.g. the resignation was taken back)?",
  xWithdrawn: "Exit withdrawn.",
  xLeaveAmount: "Unused leave paid out",
  xNoticePay: "Pay in lieu of notice",
  xGratuity: "Gratuity",
  xCompensation: "Agreed compensation",
  xLoan: "Loan balance",
  xAssets: "Unreturned assets",
  xType_resignation: "Resignation",
  xType_termination: "Termination",
  xType_contract_end: "Contract end",
  xType_retirement: "Retirement",
  xType_mutual: "Mutual agreement",
  xType_other: "Other",
  xStatus_open: "In progress",
  xStatus_settled: "Settled",
  xStatus_closed: "Closed",
  xStatus_cancelled: "Withdrawn",
  xOwner_hr: "HR",
  xOwner_manager: "Manager",
  xOwner_it: "IT",
  xOwner_finance: "Finance",
  xOwner_employee: "Employee",
  xR_career: "Career move",
  xR_pay: "Pay and benefits",
  xR_manager: "Manager",
  xR_workload: "Workload",
  xR_relocation: "Relocation",
  xR_study: "Study",
  xR_family: "Family",
  xR_other: "Other",
  x_rating_manager: "Manager",
  x_rating_pay: "Pay",
  x_rating_growth: "Growth",
  navOnboarding: "Onboarding",
  navRecruitment: "Recruitment",
  obAddStep: "Add a step",
  obDays: "Days from the start date",
  obDueAfter: "{n} days after starting",
  obDueBefore: "{n} days before starting",
  obDueFirstDay: "On the first day",
  obDueProbation: "{n} days before probation ends",
  obFromProbation: "Count from the probation end instead",
  obMine: "Getting started",
  obNewJoiners: "New joiners",
  obNone: "No onboarding in progress.",
  obNotStarted: "Joiners without onboarding",
  obOpenProfile: "Open profile",
  obOverdue: "overdue",
  obOwner: "Who does it",
  obPeople: "People",
  obStart: "Start onboarding",
  obStarted: "Onboarding started.",
  obStarts: "starts",
  obTemplate: "Checklist template",
  obTemplateHelp: "New hires get these steps with due dates worked out from their start date (or probation end). Changes apply to people who start onboarding later.",
  rqActive: "Open roles",
  rqAddCandidate: "Add a candidate",
  rqApproveOpen: "Approve and open",
  rqAsk: "Ask for a new hire",
  rqAtInterview: "at interview",
  rqAtOffer: "with an offer",
  rqCancel: "Cancel role",
  rqClosed: "Filled and cancelled",
  rqCurrentTitle: "Current job",
  rqCv: "CV",
  rqCvLink: "CV link",
  rqDaysToHire: "{n} days on average to hire",
  rqDeclined: "Offer declined",
  rqDescription: "What the role is about",
  rqHeadcount: "How many",
  rqHire: "Hire",
  rqHireAsk: "Create the employee record from this candidate and offer?",
  rqHireHelp: "Hiring creates the employee with the role, department, hiring manager, pay from the offer and three months' probation, and starts onboarding.",
  rqHired: "Hired",
  rqHiredMsg: "Hired: the employee record and onboarding are ready.",
  rqHires12: "Hires, last 12 months",
  rqHold: "Put on hold",
  rqInPipeline: "In the pipeline",
  rqInterviewer: "Interviewer",
  rqInterviews: "Interviews",
  rqMoveTo: "Stage",
  rqNew: "Open a role",
  rqNewRole: "New role",
  rqNoInterviews: "No interviews yet.",
  rqNone: "No open roles.",
  rqOffer: "Offer",
  rqOfferSent: "Offer saved as sent.",
  rqOpen: "Open the role",
  rqOpenEmployee: "Open employee",
  rqOpenPositions: "Open positions",
  rqOpened: "Role opened.",
  rqOut: "Rejected and withdrawn",
  rqRating: "Rating",
  rqReason: "Why",
  rqRecommend: "Recommendation",
  rqRejectReason: "Reason (optional)",
  rqRejected: "Candidate rejected.",
  rqReopen: "Reopen",
  rqReplacement: "Replacement",
  rqRole: "Role",
  rqRoles: "roles",
  rqScore: "Score",
  rqSendOffer: "Send offer",
  rqSource: "Source",
  rqStart: "Start date",
  rqUpcoming: "Upcoming",
  rqUpdateOffer: "Update offer",
  rqWaitingApproval: "Roles waiting for approval",
  rqWithdrawn: "Withdrew",
  stage_applied: "Applied",
  stage_screening: "Screening",
  stage_interview: "Interview",
  stage_offer: "Offer",
  stage_hired: "Hired",
  stage_rejected: "Rejected",
  stage_withdrawn: "Withdrew",
  rqStatus_requested: "Waiting for HR",
  rqStatus_open: "Open",
  rqStatus_on_hold: "On hold",
  rqStatus_filled: "Filled",
  rqStatus_cancelled: "Cancelled",
  ivKind_phone: "Phone call",
  ivKind_video: "Video call",
  ivKind_onsite: "Onsite",
  ivKind_technical: "Technical",
  ivKind_hr: "HR interview",
  ivRec_hire: "Hire",
  ivRec_maybe: "Maybe",
  ivRec_no: "No",
  ofStatus_draft: "Draft",
  ofStatus_sent: "Sent",
  ofStatus_accepted: "Accepted",
  ofStatus_declined: "Declined",
  navPerformance: "Performance",
  navTraining: "Training",
  pfReviews: "Reviews",
  pfGoals: "Goals",
  pfAckComment: "Your comment (optional)",
  pfAcknowledge: "Acknowledge",
  pfAddGoal: "Add a goal",
  pfAddMissing: "Add people who joined",
  pfAddMyGoal: "Add a goal of your own",
  pfAverage: "Average rating",
  pfClose: "Close cycle",
  pfClosed: "Cycle closed.",
  pfComments: "Overall comments",
  pfCycle: "Review cycle",
  pfDistribution: "Rating distribution",
  pfDue: "Due",
  pfEmployeeSaid: "Employee's comment",
  pfEndHelp: "Not confirming means ending the employment before probation ends; start the exit in Offboarding (probation is once only and at most 3 months under the Labour Law).",
  pfGoal: "Goal",
  pfImprovements: "To improve",
  pfLaunch: "Launch reviews",
  pfLaunched: "Reviews created.",
  pfManager: "Manager",
  pfManagerDone: "Manager reviews done",
  pfManagerDue: "Manager reviews due",
  pfMine: "My performance",
  pfMyComments: "How did it go?",
  pfMyGoals: "My goals",
  pfMyRating: "My rating",
  pfNewCycle: "New review cycle",
  pfNoGoals: "No goals yet.",
  pfNoReviews: "No reviews in this cycle yet. Launch it to create them.",
  pfNoSelfYet: "The self-review has not arrived yet; you can still write yours.",
  pfOutcome: "Probation decision",
  pfOutcome_confirm: "Confirm",
  pfOutcome_end: "Do not confirm",
  pfProbationHelp: "Picks up everyone whose probation ends between these dates.",
  pfProgress: "Progress",
  pfRating: "Rating",
  pfReviewer: "Reviewer",
  pfSelf: "Self",
  pfSelfDone: "Self-reviews done",
  pfSelfDue: "Self-reviews due",
  pfStrengths: "Strengths",
  pfSubmit: "Submit review",
  pfSubmitSelf: "Submit my self-review",
  pfWeight: "Weight %",
  pfStatus_draft: "Draft",
  pfStatus_active: "Active",
  pfStatus_closed: "Closed",
  pfKind_annual: "Annual",
  pfKind_mid_year: "Mid-year",
  pfKind_quarterly: "Quarterly",
  pfKind_probation: "Probation",
  pfR_self: "Waiting for self-review",
  pfR_manager: "Waiting for manager",
  pfR_completed: "Completed",
  pfR_acknowledged: "Acknowledged",
  pfScale_5: "Outstanding",
  pfScale_4: "Exceeds expectations",
  pfScale_3: "Meets expectations",
  pfScale_2: "Partly meets",
  pfScale_1: "Does not meet",
  gStatus_active: "Active",
  gStatus_achieved: "Achieved",
  gStatus_missed: "Missed",
  gStatus_dropped: "Dropped",
  trAskFor: "Ask for a course…",
  trCancelSession: "Cancel session",
  trCapacity: "Places",
  trCatalogue: "Course catalogue",
  trCompletions: "completions",
  trCost: "Cost per person (EGP)",
  trCourse: "Course",
  trEnroll: "Enrol",
  trEnrollMore: "Enrol more people",
  trEnrolled: "Enrolled.",
  trExpired: "Expired",
  trExpires: "Expires",
  trExpiring: "Certificates expiring in 60 days",
  trExpiringTitle: "Certificates to renew",
  trHours: "Hours",
  trHoursYear: "Training hours this year",
  trMandatory: "Mandatory",
  trMarkDone: "Mark completed",
  trMine: "My training",
  trMissing: "Still needed",
  trNewCourse: "New course",
  trNewSession: "Plan a session",
  trNoSessions: "No sessions yet.",
  trNoShow: "Did not attend",
  trNoneMine: "No training yet.",
  trRecorded: "Recorded.",
  trRequests: "Training requests",
  trScore: "Score",
  trSessionAdded: "Session planned.",
  trSessionDone: "Mark session done",
  trSessions: "Sessions",
  trSpendYear: "Spend this year",
  trTrainer: "Trainer",
  trValid: "certificate valid {n} months",
  trValidity: "Certificate valid (months)",
  trWhere: "Where",
  trS_planned: "Planned",
  trS_done: "Done",
  trS_cancelled: "Cancelled",
  trR_requested: "Requested",
  trR_enrolled: "Enrolled",
  trR_completed: "Completed",
  trR_no_show: "Did not attend",
  trR_declined: "Declined",
  trCat_induction: "Induction",
  trCat_compliance: "Compliance",
  trCat_safety: "Safety",
  trCat_skills: "Skills",
  trCat_technical: "Technical",
  trCat_leadership: "Leadership",
  trCat_language: "Language",
  trCat_other: "Other",
  apprReviews: "Reviews to write",
  apprOpenPerformance: "Open performance",
  apprOpenTraining: "Open training",
  navRelations: "Employee relations",
  navEngagement: "Engagement",
  navAnalytics: "People analytics",
  delete: "Delete",
  seeAll: "See all",
  erOpen: "Open cases",
  erGrievancesOpen: "Open grievances",
  erNone: "No cases.",
  erCase: "Case",
  erConfidential: "Confidential",
  erConfidentialLong: "Confidential: only HR can see it, not the manager",
  erNew: "Open a case",
  erCategory: "Category",
  erIncident: "Date of incident",
  erWhatHappened: "What happened",
  erOpenBtn: "Open case",
  erRules: "Egyptian Labour Law 14/2025: the employee must be told in writing and heard before any penalty; a pay deduction is at most 5 days' basic pay per violation and per month.",
  erHeard: "Hearing held",
  erDays: "days",
  erInPayroll: "deducted in payroll",
  erTimeline: "Timeline",
  erAddNote: "Add to the timeline",
  erDecide: "Decision",
  erOutcome: "Outcome",
  erDaysBasic: "Days of basic pay (max 5)",
  erHearFirst: "Record a hearing note before deciding a penalty.",
  erDecideBtn: "Record decision",
  erDecided: "Decision recorded. The employee can see it.",
  erClose: "Close case",
  erClosedMsg: "Case closed.",
  erMine: "Grievances and cases",
  erRaise: "Raise a grievance",
  erRaiseHelp: "Only HR sees this. Your manager is not told unless HR needs to involve them.",
  erRaised: "Sent to HR in confidence.",
  erKind_disciplinary: "Disciplinary",
  erKind_grievance: "Grievance",
  erStatus_open: "Open",
  erStatus_investigating: "Investigating",
  erStatus_decided: "Decided",
  erStatus_closed: "Closed",
  erCat_attendance: "Attendance",
  erCat_conduct: "Conduct",
  erCat_performance: "Performance",
  erCat_safety: "Safety",
  erCat_policy: "Policy breach",
  erCat_harassment: "Harassment",
  erCat_pay: "Pay",
  erCat_workload: "Workload",
  erCat_manager: "Manager",
  erCat_other: "Other",
  erOut_no_action: "No action",
  erOut_verbal_warning: "Verbal warning",
  erOut_written_warning: "Written warning",
  erOut_deduction: "Pay deduction",
  erOut_suspension: "Suspension",
  erOut_final_warning: "Final warning",
  erOut_dismissal_referral: "Referred for dismissal",
  erOut_resolved: "Resolved",
  erOut_partly_upheld: "Partly upheld",
  erOut_not_upheld: "Not upheld",
  erNote_note: "Note",
  erNote_notice: "Written notice",
  erNote_hearing: "Hearing",
  erNote_evidence: "Evidence",
  erNote_decision: "Decision",
  erNote_status: "Status",
  engTab_news: "Announcements",
  engTab_surveys: "Surveys",
  engTab_kudos: "Recognition",
  annNew: "New announcement",
  annEdit: "Edit announcement",
  annBody: "Message",
  annExpires: "Show until (optional)",
  annPinned: "Pinned",
  annPublish: "Publish",
  annNone: "No announcements yet.",
  annNewBadge: "New",
  annUntil: "until",
  annMarkRead: "Mark as read",
  annDeleteConfirm: "Delete this announcement?",
  annHome: "Company news",
  svForYou: "Surveys for you",
  svNoneOpen: "Nothing to answer right now.",
  svCloses: "Closes",
  svAnonymous: "Anonymous",
  svAnswer: "Answer",
  svAnsweredN: "You have answered {n} survey(s). Thank you.",
  svThanks: "Thank you. Your answers were saved.",
  svManage: "Manage surveys",
  svNew: "New survey",
  svQuestions: "Questions",
  svLaunch: "Launch",
  svLaunched: "Survey is open to everyone.",
  svCloseBtn: "Close",
  svResults: "Results",
  svStatus_draft: "Draft",
  svStatus_open: "Open",
  svStatus_closed: "Closed",
  svAnonNote: "Anonymous: your answers are stored without your name. Results show only when at least 3 people answer.",
  svNamedNote: "This survey is not anonymous; HR can see who answered.",
  svMissing: "Please answer",
  svScaleEnds: "1 = strongly disagree · 5 = strongly agree",
  svEnpsEnds: "0 = not at all likely · 10 = extremely likely",
  svSubmit: "Submit answers",
  svIntro: "Introduction",
  svAnonLong: "Anonymous (cannot be changed after saving)",
  svQuestion: "Question",
  svOptions: "Options, separated by commas",
  svKind_scale: "1–5 scale",
  svKind_enps: "eNPS 0–10",
  svKind_choice: "Choice",
  svKind_text: "Comment",
  svRate: "{n} of {m} people answered.",
  svHidden: "Results show once at least 3 people have answered, to protect anonymity.",
  svEnpsHelp: "% promoters (9–10) minus % detractors (0–6)",
  svByDept: "By department",
  kdGive: "Recognise a colleague",
  kdTo: "Colleague",
  kdValue: "For",
  kdMessage: "Message",
  kdSend: "Send",
  kdSent: "Sent. Everyone can see it in the feed.",
  kdFeed: "Recognition feed",
  kdNone: "No recognition yet. Be the first!",
  kdV_teamwork: "Teamwork",
  kdV_customer: "Customer focus",
  kdV_ownership: "Ownership",
  kdV_innovation: "Innovation",
  kdV_excellence: "Excellence",
  kdV_helpfulness: "Helpfulness",
  anHeadcount: "Headcount at period end",
  anHires: "Hires",
  anDaysToHire: "Avg days to hire",
  anApplications: "Applications",
  anOfferAccept: "Offer acceptance",
  anAvgRating: "Average rating",
  anGoals: "Goals achieved",
  anTrainingHours: "Training hours",
  anPerHead: "per person",
  anTrainingSpend: "Training spend",
  anCompletions: "Completions",
  anNoShows: "No-shows",
  anExpiring: "Certificates expiring (60 days)",
  anEnps: "eNPS",
  anParticipation: "Survey participation",
  anKudos: "Kudos given",
  anDisciplinary: "Disciplinary cases",
  anGrievances: "Grievances",
  anOpenCases: "Open now",
  anAppsBySource: "Applications by source",
  anRatingSpread: "Rating spread (latest cycle)",
  anRatingByDept: "Average rating by department",
  anKudosByValue: "Recognition by value",
  anCaseOutcomes: "Case outcomes",
  anNote: "Performance shows the latest finished review cycle; engagement the latest survey with an eNPS question. Everything else follows the chosen period.",
  fileAttach: "Attach file",
  fileReplace: "Replace",
  fileUploading: "Uploading…",
  fileRemoveConfirm: "Remove this file?",
  fileLabel: "File (PDF, image, Word or Excel, up to 10 MB)",
  fileFailedLater: "The file could not be uploaded; attach it from the list.",
  rqCvFile: "CV",
  impStep1: "Download the Excel template. Fill one employee per row on the Employees sheet; the Guide sheet explains every column. Only first name, last name and hire date are required.",
  impTemplate: "Download Excel template",
  impStep2: "Pick the filled file (.xlsx or .csv). PeopleCore checks every row first — nothing is saved until you confirm.",
  impChoose: "Choose file",
  impChecking: "Checking rows…",
  impEmpty: "No employee rows were found under the header row.",
  impTooMany: "Import at most 2,000 rows at a time; split the file.",
  impOldXls: "Old .xls files are not supported; in Excel use Save As → Excel Workbook (.xlsx).",
  impUnknownCols: "These columns are not recognised and will be ignored",
  impReady: "ready",
  impWithNotes: "with notes",
  impWillFail: "need fixing",
  impFixHint: "Rows that need fixing will be skipped. Download them, correct them in Excel and import that file afterwards — or fix the whole file and choose it again.",
  impAllGood: "Every row passed the check.",
  impDone: "Import finished. New employees are in the list; their pay, insurance, bank details, manager and leave balance are set where given.",
  impProblem: "Problem or note",
  impDownloadProblems: "Download rows to fix (.xlsx)",
  impRun: "Import {n} employees",
  roleHelp: "Change a role from the list. It takes effect the next time that person opens PeopleCore. You cannot change your own role, and the company always keeps at least one HR admin.",
  roleChanged: "{name} is now {role}.",
  you: "you",
  switchOff: "Switch off access",
  switchOn: "Restore access",
  accessOffConfirm: "Switch off this person's access? Their employee record stays; they just cannot sign in to this company.",
  accessOff: "Access switched off.",
  accessOn: "Access restored.",
};

type Dict = typeof en;
export type StringKey = keyof Dict;

const ar: Dict = {
  appName: "PeopleCore",
  tagline: "نواة واحدة للموارد البشرية. اختر الوحدات التي تحتاجها.",
  switchLang: "English",
  loading: "جارٍ التحميل…",
  signInTitle: "تسجيل الدخول إلى PeopleCore",
  signUpTitle: "إنشاء حسابك",
  email: "البريد الإلكتروني للعمل",
  password: "كلمة المرور",
  passwordHint: "٨ أحرف على الأقل.",
  fullName: "الاسم الكامل",
  signIn: "تسجيل الدخول",
  signUp: "إنشاء حساب",
  haveAccount: "لديك حساب بالفعل؟",
  noAccount: "جديد على PeopleCore؟",
  switchToSignIn: "تسجيل الدخول",
  switchToSignUp: "إنشاء حساب",
  checkEmail: "تحقق من بريدك الإلكتروني لتأكيد الحساب، ثم سجّل الدخول.",
  signOut: "تسجيل الخروج",
  setupTitle: "إعداد شركتك",
  stepCompany: "الشركة",
  stepPreset: "نقطة البداية",
  stepModules: "الوحدات",
  companyName: "اسم الشركة (بالإنجليزية)",
  companyNameAr: "اسم الشركة (بالعربية)",
  optional: "اختياري",
  country: "الدولة",
  defaultLanguage: "اللغة الافتراضية",
  english: "الإنجليزية",
  arabic: "العربية",
  next: "التالي",
  back: "السابق",
  presetHelp: "اختر الأقرب لشركتك. يمكنك تعديل أي وحدة في الخطوة التالية أو لاحقًا.",
  modulesHelp: "اختر الوحدات التي تحتاجها الشركة. الوحدات المطلوبة لها تُختار تلقائيًا.",
  requiredBy: "مطلوبة لـ",
  alwaysOn: "مفعّلة دائمًا",
  arrives: "تصل في",
  createCompany: "إنشاء الشركة",
  creating: "جارٍ إنشاء شركتك…",
  nameRequired: "أدخل اسم الشركة.",
  navHome: "الرئيسية",
  navOrganisation: "الهيكل التنظيمي",
  navMembers: "المستخدمون والأدوار",
  navModules: "الوحدات",
  navWorkspace: "مساحة العمل",
  navYourModules: "وحداتك",
  greeting: "مرحبًا",
  homeIntro: "تم إعداد شركتك. هذه هي الوحدات المفعّلة.",
  modulesOn: "وحدة مفعّلة",
  yourRole: "دورك",
  comingSoonTitle: "هذه الوحدة قيد الإعداد",
  comingSoonBody: "الوحدة مفعّلة لشركتك وستظهر هنا عند الانتهاء من مرحلتها.",
  departments: "الإدارات",
  addDepartment: "إضافة إدارة",
  departmentName: "اسم الإدارة (بالإنجليزية)",
  departmentNameAr: "اسم الإدارة (بالعربية)",
  parentDepartment: "تابعة لـ",
  none: "لا يوجد",
  save: "حفظ",
  cancel: "إلغاء",
  noDepartments: "لا توجد إدارات بعد. أضف أول إدارة.",
  branches: "الفروع",
  headOffice: "المقر الرئيسي",
  members: "الأشخاص المصرّح لهم",
  role: "الدور",
  status: "الحالة",
  active: "نشط",
  inactive: "غير نشط",
  modulesTitle: "الوحدات",
  modulesPageHelp: "فعّل الوحدات أو أوقفها للشركة كلها. إيقاف وحدة يخفيها مع الاحتفاظ ببياناتها.",
  saveChanges: "حفظ التغييرات",
  saved: "تم الحفظ",
  noPermission: "ليست لديك صلاحية لتغيير هذا.",
  errorGeneric: "حدث خطأ ما. حاول مرة أخرى.",
  navEmployees: "الموظفون",
  navDocuments: "مستندات التعيين",
  employees: "الموظفون",
  searchEmployees: "ابحث بالاسم أو الرقم أو الوظيفة",
  allStatuses: "كل الحالات",
  addEmployee: "إضافة موظف",
  importEmployees: "استيراد",
  noEmployees: "لا يوجد موظفون بعد. أضف أول موظف أو استورد ملفًا.",
  employeeNumber: "رقم الموظف",
  autoNumber: "اتركه فارغًا للترقيم التلقائي",
  jobTitle: "المسمى الوظيفي",
  jobTitleAr: "المسمى الوظيفي (بالعربية)",
  department: "الإدارة",
  branch: "الفرع",
  manager: "المدير المباشر",
  hireDate: "تاريخ التعيين",
  statusActive: "نشط",
  statusProbation: "في فترة الاختبار",
  statusStarting: "يبدأ قريبًا",
  statusTerminated: "غادر",
  firstName: "الاسم الأول",
  lastName: "اسم العائلة",
  firstNameAr: "الاسم الأول (بالعربية)",
  lastNameAr: "اسم العائلة (بالعربية)",
  gender: "النوع",
  female: "أنثى",
  male: "ذكر",
  dateOfBirth: "تاريخ الميلاد",
  nationality: "الجنسية",
  egyptian: "مصري",
  otherNationality: "أخرى (رمز الدولة، مثل SA)",
  nationalId: "الرقم القومي",
  nationalIdHint: "١٤ رقمًا",
  mobile: "الهاتف المحمول",
  workEmail: "البريد الإلكتروني للعمل",
  personalEmail: "البريد الإلكتروني الشخصي",
  contractType: "نوع العقد",
  contractEnd: "تاريخ انتهاء العقد",
  probationEnd: "نهاية فترة الاختبار",
  probationHint: "ثلاثة أشهر على الأكثر من تاريخ التعيين",
  grade: "الدرجة",
  fte: "نسبة الدوام",
  hireSource: "مصدر التعيين",
  sectionPerson: "البيانات الشخصية",
  sectionJob: "الوظيفة",
  sectionContract: "العقد",
  backToList: "كل الموظفين",
  jobHistory: "السجل الوظيفي",
  from: "من",
  to: "إلى",
  change: "التغيير",
  current: "الحالي",
  documents: "المستندات",
  docStatus_pending: "قيد الانتظار",
  docStatus_overdue: "متأخر",
  docStatus_received: "تم الاستلام",
  docStatus_expiring: "ينتهي قريبًا",
  docStatus_expired: "منتهي",
  docStatus_waived: "تم الإعفاء",
  docStatus_rejected: "مرفوض",
  dueDate: "الموعد",
  expiryDate: "تاريخ الانتهاء",
  receivedOn: "تاريخ الاستلام",
  markReceived: "تسجيل الاستلام",
  waive: "إعفاء",
  undo: "تراجع",
  required: "إلزامي",
  showAll: "عرض الكل",
  needsAttention: "يحتاج متابعة",
  allDone: "لا شيء يحتاج متابعة. كل المستندات الإلزامية مكتملة.",
  overdueCount: "متأخر",
  pendingCount: "قيد الانتظار",
  expiringCount: "ينتهي قريبًا",
  importTitle: "استيراد الموظفين",
  importStep1: "حمّل النموذج واملأه في Excel، موظف واحد في كل صف.",
  downloadTemplate: "تحميل النموذج (CSV)",
  importStep2: "في Excel اختر ملف ← حفظ باسم ← CSV UTF-8، ثم اختر الملف هنا.",
  chooseFile: "اختر ملف CSV",
  rowsFound: "صفًا",
  runImport: "استيراد الصفوف",
  importing: "جارٍ الاستيراد…",
  imported: "تم استيراده",
  failed: "فشل",
  row: "الصف",
  error: "الخطأ",
  close: "إغلاق",
  terminated: "غادر في",
  sampleTitle: "جرّب PeopleCore ببيانات تجريبية",
  sampleBody: "حمّل شركة تجريبية: 42 موظفًا في 7 إدارات وفرعين، مع مديرين وحالات ترك عمل وإعادة تعيين، وإجازات بكل الحالات، وحضور 45 يومًا، وخطابات وسياسات وطلبات بانتظارك. سيتم ربطك بمدير الموارد البشرية التجريبي لتجربة شاشات الموظفين أيضًا. يمكن حذف كل ذلك بخطوة واحدة.",
  sampleLoad: "إضافة البيانات التجريبية",
  sampleLoaded: "موظفًا تجريبيًا تمت إضافتهم.",
  sampleOnTitle: "البيانات التجريبية مُضافة",
  sampleOnBody: "استكشف كل الشاشات بالشركة التجريبية. أعد تحميلها للبدء من جديد بتواريخ اليوم، أو احذفها قبل استيراد موظفيك الحقيقيين؛ سجلاتك الحقيقية لا تتأثر أبدًا.",
  sampleRemove: "حذف البيانات التجريبية",
  sampleRemoveConfirm: "حذف كل الموظفين التجريبيين؟",
  sampleRemoved: "موظفًا تجريبيًا تم حذفهم.",
  editDetails: "تعديل البيانات",
  changeJob: "تغيير الوظيفة",
  changeManager: "تغيير المدير",
  endEmployment: "إنهاء الخدمة",
  effectiveFrom: "اعتبارًا من",
  reason: "السبب",
  reason_promotion: "ترقية",
  reason_transfer: "نقل",
  reason_role_change: "تغيير الدور",
  reason_contract_change: "تغيير العقد",
  reason_correction: "تصحيح",
  reason_hire: "تعيين",
  reason_rehire: "إعادة تعيين",
  keepCurrent: "كما هو",
  noManager: "بدون مدير",
  lastDay: "آخر يوم عمل",
  leavingReason: "سبب ترك العمل",
  regrettable: "خسارة للشركة (كنا نرغب في بقائه)",
  rehireEligible: "مؤهل لإعادة التعيين",
  endEmploymentHelp: "تنتهي الوظيفة والعقد وخطوط التبعية في آخر يوم. سيحتاج من كانوا يتبعونه إلى مدير جديد.",
  confirmEnd: "إنهاء الخدمة",
  done: "تم الحفظ",
  navSettings: "الإعدادات",
  customFields: "حقول مخصصة",
  customFieldsHelp: "أضف حقولك الخاصة لسجلات الموظفين. تظهر في كل ملف.",
  fieldLabel: "الاسم (بالإنجليزية)",
  fieldLabelAr: "الاسم (بالعربية)",
  fieldType: "النوع",
  type_text: "نص",
  type_number: "رقم",
  type_date: "تاريخ",
  type_boolean: "نعم / لا",
  type_select: "قائمة اختيارات",
  fieldOptions: "الاختيارات، مفصولة بفواصل",
  appliesToRecord: "يخص",
  entity_person: "الشخص",
  entity_employment: "التعيين",
  addField: "إضافة حقل",
  noFields: "لا توجد حقول مخصصة بعد.",
  activeLabel: "مستخدم",
  hiringDocsList: "قائمة مستندات التعيين",
  hiringDocsHelp: "اختر المستندات المطلوبة من الموظفين الجدد. أوقف ما لا تحتاجه.",
  documentName: "اسم المستند (بالإنجليزية)",
  documentNameAr: "اسم المستند (بالعربية)",
  appliesTo: "مطلوب من",
  applies_all: "الجميع",
  applies_male: "الذكور",
  applies_female: "الإناث",
  applies_egyptian: "المصريين",
  applies_non_egyptian: "غير المصريين",
  hasExpiry: "له تاريخ انتهاء",
  addDocument: "إضافة مستند",
  linkedEmployee: "سجل الموظف",
  notLinked: "غير مرتبط",
  linkHelp: "اربط حساب الدخول بسجل الموظف ليرى صاحبه ملفه.",
  yes: "نعم",
  no: "لا",
  customDetails: "بيانات إضافية",
  navMyFile: "ملفي",
  navLeave: "الإجازات",
  navMyLeave: "إجازاتي",
  tabRequests: "الطلبات",
  tabBalances: "الأرصدة",
  tabHolidays: "العطلات",
  newRequest: "طلب إجازة جديد",
  employee: "الموظف",
  leaveType: "نوع الإجازة",
  startDate: "أول يوم",
  endDate: "آخر يوم",
  days: "الأيام",
  note: "ملاحظة",
  approveNow: "اعتماد مباشرة",
  submitRequest: "إرسال الطلب",
  approve: "اعتماد",
  reject: "رفض",
  cancelRequest: "إلغاء الطلب",
  leaveStatus_pending: "قيد الانتظار",
  leaveStatus_approved: "معتمد",
  leaveStatus_rejected: "مرفوض",
  leaveStatus_cancelled: "ملغي",
  pendingApproval: "بانتظار القرار",
  noPending: "لا توجد طلبات بانتظار القرار.",
  allRequests: "كل الطلبات",
  noRequests: "لا توجد طلبات إجازة بعد.",
  year: "السنة",
  entitlement: "الاستحقاق",
  carriedOver: "مُرحّل",
  adjusted: "تسوية",
  taken: "مستهلك",
  pendingDays: "قيد الانتظار",
  remaining: "المتبقي",
  loadBalances: "عرض الأرصدة",
  addAdjustment: "إضافة رصيد افتتاحي أو تسوية",
  adjustmentDays: "الأيام (استخدم السالب للخصم)",
  adjustmentReason: "السبب",
  publicHolidays: "العطلات الرسمية",
  companyHolidays: "عطلات الشركة",
  addHoliday: "إضافة عطلة للشركة",
  holidayName: "اسم العطلة",
  date: "التاريخ",
  tentative: "الموعد غير مؤكد",
  noHolidays: "لم تتم إضافة عطلات للشركة.",
  leaveRules: "يتم التحقق من القواعد عند الإرسال: الرصيد وأيام العمل والعطلات.",
  myBalances: "أرصدتي",
  myRequests: "طلباتي",
  remove: "حذف",
  daysUnit: "يوم",
  navAttendance: "الحضور والانصراف",
  tabDay: "اليوم",
  tabSummary: "الملخص الشهري",
  tabImport: "استيراد",
  att_present: "حاضر",
  att_late: "متأخر",
  att_on_leave: "في إجازة",
  att_absent: "غائب",
  att_day_off: "يوم راحة",
  att_upcoming: "قادم",
  checkIn: "الحضور",
  checkOut: "الانصراف",
  minutesLate: "دقائق التأخير",
  month: "الشهر",
  workingDays: "أيام العمل",
  presentDays: "حضور",
  lateDays: "تأخير",
  lateMinutes: "تأخير (دقيقة)",
  leaveDays: "إجازة",
  absentDays: "غياب",
  missingOut: "بدون انصراف",
  attImportHelp1: "ملف يومي: صف لكل موظف في كل يوم يحتوي employee_number و date و check_in و check_out.",
  attImportHelp2: "ملف جهاز البصمة: صف لكل بصمة يحتوي employee_number و punch (التاريخ والوقت). أول بصمة في اليوم هي الحضور وآخر بصمة هي الانصراف.",
  dailyTemplate: "نموذج يومي",
  punchTemplate: "نموذج البصمات",
  edit: "تعديل",
  noEmployeesDay: "لا يوجد موظفون في هذا اليوم.",
  navApprovals: "الموافقات",
  navTeam: "فريقي",
  waitingForYou: "بانتظار قرارك",
  openApprovals: "فتح الموافقات",
  needsAttentionTitle: "يحتاج متابعة",
  alert_contract_end: "انتهاء العقد",
  alert_probation_end: "انتهاء فترة الاختبار",
  alert_document_expiring: "مستند ينتهي",
  alert_document_expired: "مستند منتهي",
  alert_documents_overdue: "مستندات تعيين متأخرة",
  alert_birthday: "عيد ميلاد",
  inDays: "بعد {n} يوم",
  today: "اليوم",
  daysAgo: "منذ {n} يوم",
  noApprovals: "لا شيء بانتظارك.",
  stage_manager: "مرحلة المدير",
  stage_hr: "مرحلة الموارد البشرية",
  decisionNote: "ملاحظة (اختياري)",
  delegateTitle: "مسافر؟ سلّم موافقاتك لزميل",
  delegateTo: "الزميل البديل",
  delegateSave: "تسليم",
  delegated: "تم تسليم الموافقات.",
  noTeam: "لا يوجد من يتبعك بعد.",
  inviteTitle: "دعوة مستخدمين",
  inviteHelp: "يسجل الشخص على عنوان التطبيق بهذا البريد وينضم لشركتك تلقائيًا.",
  inviteEmail: "البريد الإلكتروني",
  sendInvite: "إنشاء دعوة",
  pendingInvites: "دعوات مفتوحة",
  copyMessage: "نسخ الرسالة",
  copied: "تم النسخ",
  revoke: "إلغاء",
  inviteMessage: "تمت دعوتك إلى {company} على PeopleCore. افتح {link} وأنشئ حسابًا بالبريد {email} وستنضم تلقائيًا.",
  joinTitle: "لديك دعوة",
  joinBody: "انضم إلى شركتك على PeopleCore.",
  join: "انضمام",
  orSetUp: "أو أنشئ شركة جديدة بدلًا من ذلك",
  approvalChain: "اعتماد الإجازات",
  chain_manager_hr: "المدير ثم الموارد البشرية",
  chain_manager: "المدير فقط",
  chain_hr: "الموارد البشرية فقط",
  chain_none: "بدون اعتماد (تُعتمد عند الطلب)",
  approvalChainHelp: "إذا لم يكن للموظف مدير لديه حساب، يذهب الطلب مباشرة للموارد البشرية.",
  navLetters: "الخطابات",
  navPolicies: "السياسات",
  tabIssue: "إصدار",
  tabLetterRequests: "الطلبات",
  tabTemplates: "النماذج",
  tabHistory: "السجل",
  letterTemplate: "الخطاب",
  fillBlanks: "أكمل الفراغات",
  preview: "معاينة",
  printAndIssue: "إصدار وطباعة",
  printAgain: "طباعة",
  issued: "تم الإصدار",
  reference: "الرقم المرجعي",
  purpose: "الغرض",
  requestLetter: "طلب خطاب",
  requestSent: "تم إرسال الطلب للموارد البشرية.",
  myLetters: "خطاباتي",
  decline: "رفض",
  noLetterRequests: "لا توجد طلبات خطابات.",
  noLetters: "لم يتم إصدار خطابات بعد.",
  bodyEn: "النص (بالإنجليزية)",
  bodyAr: "النص (بالعربية)",
  selfService: "يمكن للموظفين طلبه",
  placeholdersHelp: "الكلمات بين الأقواس تُملأ تلقائيًا مثل {employee_name} و{job_title} و{hire_date}. أي {كلمة} أخرى تصبح فراغًا يُملأ عند الإصدار.",
  signatoryName: "التوقيع (الاسم)",
  signatoryTitle: "التوقيع (الوظيفة)",
  referencePrefix: "بادئة الرقم المرجعي",
  letterSettings: "إعدادات الخطابات",
  publishPolicy: "نشر سياسة",
  newVersion: "نشر إصدار جديد",
  policyTitle: "العنوان (بالإنجليزية)",
  policyTitleAr: "العنوان (بالعربية)",
  policyBody: "نص السياسة (بالإنجليزية)",
  policyBodyAr: "نص السياسة (بالعربية)",
  requiresAck: "يجب أن يقر الموظفون بها",
  publish: "نشر",
  acknowledged: "تم الإقرار",
  acknowledge: "قرأت هذه السياسة وأوافق عليها",
  toAcknowledge: "بانتظار الإقرار",
  version: "الإصدار",
  progress: "نسبة الإقرار",
  noPolicies: "لم يتم نشر سياسات بعد.",
  readPolicy: "قراءة",
  linkFirst: "اطلب من الموارد البشرية ربط حسابك بسجل الموظف أولًا.",
  nameEn: "الاسم (بالإنجليزية)",
  nameAr: "الاسم (بالعربية)",
  pending: "قيد الانتظار",
  navDashboard: "لوحة المؤشرات",
  navMyLetters: "خطاباتي",
  dashHeadcount: "عدد الموظفين",
  dashVsYearAgo: "مقارنة بالعام الماضي",
  dashHires12: "التعيينات في آخر 12 شهرًا",
  dashLast90: "في آخر 90 يومًا",
  dashTurnover: "معدل الاستقالات خلال 12 شهرًا",
  dashAllLeavers: "كل حالات ترك العمل",
  dashEarlyLeavers: "تركوا العمل خلال السنة الأولى",
  dashEarlyHint: "آخر 12 شهرًا",
  dashTenure: "متوسط مدة الخدمة",
  dashYears: "سنة",
  dashAvgAge: "متوسط العمر",
  dashProbation: "تحت الاختبار",
  dashStartingSoon: "يبدؤون قريبًا",
  dashOnLeave: "في إجازة اليوم",
  dashPendingLeave: "طلبات بانتظار القرار",
  dashAttendance: "نسبة الحضور هذا الشهر",
  dashLate: "التأخير",
  dashDocsOverdue: "مستندات تعيين ناقصة",
  dashDocsHint: "موظفون لديهم مستندات متأخرة",
  dashLt1: "أقل من سنة",
  dash1to3: "1–3 سنوات",
  dash3to5: "3–5 سنوات",
  dash5to10: "5–10 سنوات",
  dash10plus: "أكثر من 10 سنوات",
  dashSampleNote: "{n} من هؤلاء بيانات تجريبية. احذف البيانات التجريبية من الصفحة الرئيسية بعد إضافة فريقك الحقيقي.",
  dashHeadcountTrend: "عدد الموظفين في آخر 12 شهرًا",
  dashShowTable: "عرض كجدول",
  dashShowChart: "عرض كرسم بياني",
  dashHiresLeavers: "التعيينات وترك العمل شهريًا",
  dashHires: "تعيينات",
  dashLeavers: "ترك العمل",
  dashVoluntary: "استقالة",
  dashByDepartment: "حسب الإدارة",
  dashByTenure: "حسب مدة الخدمة",
  dashByAge: "حسب العمر",
  dashByGender: "حسب النوع",
  dashByContract: "حسب نوع العقد",
  dashByNationality: "حسب الجنسية",
  dashEgyptian: "مصريون",
  dashOther: "جنسيات أخرى",
  dashUnknown: "غير مسجل",
  dashMonth: "الشهر",
  dashPowerBi: "Power BI",
  dashPowerBiText: "أنشئ تقاريرك في Power BI من نفس الأرقام. يشرح الدليل كيفية إنشاء حساب للقراءة فقط والاتصال.",
  dashPowerBiGuide: "فتح دليل Power BI",
  dashAsOf: "الأرقام حتى تاريخ",
  navMe: "أنا",
  navAlerts: "التنبيهات",
  navMyRequests: "طلباتي",
  dates: "التواريخ",
  apprWaiting: "بانتظارك",
  apprCover: "التغطية",
  apprLeave: "طلبات الإجازة",
  apprDetails: "تعديل البيانات الشخصية",
  apprLetters: "طلبات الخطابات",
  apprOpenLetters: "فتح الخطابات",
  apprNotOwn: "يجب أن يعتمد زميل آخر في الموارد البشرية التعديلات على سجلك",
  apprHistoryTitle: "قراراتك في آخر 90 يومًا",
  apprNoHistory: "لم تتخذ أي قرار في آخر 90 يومًا.",
  apprYourDecision: "قرارك",
  apprDecidedOn: "تاريخ القرار",
  apprPassedOn: "تمت الموافقة وأُرسل للموارد البشرية",
  apprCoverList: "التغطية الحالية والقادمة",
  apprNoCover: "لا توجد تغطية.",
  apprCoveredBy: "يغطي موافقاتك",
  apprCoveringFor: "أنت تغطي موافقات",
  marital_single: "أعزب",
  marital_married: "متزوج",
  marital_divorced: "مطلق",
  marital_widowed: "أرمل",
  pfAddress: "العنوان",
  pfEmergencyName: "جهة اتصال للطوارئ",
  pfEmergencyPhone: "هاتف الطوارئ",
  pfMarital: "الحالة الاجتماعية",
  pfEducation: "المؤهل الدراسي",
  myDetails: "بياناتي الشخصية",
  requestChange: "طلب تعديل",
  changeWaiting: "بانتظار الموارد البشرية:",
  detailsHelp: "تراجع الموارد البشرية التعديلات قبل ظهورها في سجلك.",
  noteForHr: "ملاحظة للموارد البشرية (اختياري)",
  sendToHr: "إرسال للموارد البشرية",
  myRequestsAll: "كل ما طلبته",
  detailsChange: "البيانات الشخصية",
  noRequestsYet: "لم ترسل أي طلبات بعد.",
  letterStatus_pending: "قيد الانتظار",
  letterStatus_issued: "صدر",
  letterStatus_declined: "مرفوض",
  forYou: "لك",
  nothingForYou: "لا يوجد ما يحتاج انتباهك.",
  rem_policy_ack: "يرجى الإقرار",
  rem_document_expiring: "مستندك ينتهي",
  rem_document_expired: "مستندك منتهي",
  rem_leave_approved: "تمت الموافقة على الإجازة",
  rem_leave_rejected: "لم تتم الموافقة على الإجازة",
  rem_letter_ready: "الخطاب جاهز",
  rem_letter_declined: "رُفض طلب الخطاب",
  rem_profile_approved: "تم تحديث بياناتك",
  rem_profile_rejected: "رُفض تعديل البيانات",
  rem_probation_end: "تنتهي فترة اختبارك",
  remGo_policies: "السياسات",
  remGo_myleave: "إجازاتي",
  remGo_myletters: "خطاباتي",
  remGo_myrequests: "طلباتي",
  alertsPeople: "مواعيد قادمة لفريقك",
  alertsFilter: "تصفية التنبيهات",
  alertsAll: "الكل",
  alertsNone: "لا توجد مواعيد قادمة.",
  alertsDocsCount: "مستندات",
  alertsSettings: "إعدادات التذكير",
  alertsContractDays: "انتهاء العقد: عدد الأيام مسبقًا",
  alertsProbationDays: "انتهاء الاختبار: عدد الأيام مسبقًا",
  alertsDocumentDays: "انتهاء المستندات: عدد الأيام مسبقًا",
  alertsBirthdays: "إظهار أعياد الميلاد (خلال 7 أيام)",
  alertsInAppOnly: "تظهر التنبيهات داخل التطبيق حاليًا. تبدأ التذكيرات بالبريد بعد إعداد بريد الشركة.",
  alertsSeeAll: "عرض كل التنبيهات ({n})",
  sampleReset: "إعادة تحميل البيانات التجريبية",
  dashLast12: "آخر 12 شهرًا",
  dashYtd: "منذ بداية العام",
  dashQuarter: "ربع سنوي",
  dashMonthly: "شهري",
  dashPeriod: "الفترة",
  dashYear: "السنة",
  dashFullYear: "السنة كاملة",
  dashTurnoverP: "معدل الاستقالات",
  dashPerYear: "سنويًا",
  dashLeaveDays: "أيام الإجازات المأخوذة",
  dashAttendanceP: "نسبة الحضور",
  dashSinceStart: "منذ بداية الفترة",
  dashStartingSoonT: "يبدؤون قريبًا",
  dashInPeriod: "خلال الفترة",
  dashAtEnd: "في نهاية الفترة",
  dashRightNow: "الآن",
  dashChosenPeriod: "الفترة المختارة",
  dashMixAt: "القوى العاملة في {date}",
  search: "بحث",
  navPayroll: "الرواتب",
  navMyPayslips: "قسائم راتبي",
  payRuns: "مسيرات الرواتب",
  paySetup: "إعداد الرواتب",
  payElements: "بنود الراتب",
  paySettings: "إعدادات الرواتب",
  payNewRun: "إعداد مسير رواتب",
  payPrepare: "إعداد",
  payNoRuns: "لا توجد مسيرات بعد. اختر شهرًا وابدأ.",
  payPeople: "عدد الموظفين",
  payNet: "صافي الراتب",
  payGross: "إجمالي الراتب",
  payTax: "ضريبة كسب العمل",
  payInsurance: "التأمينات الاجتماعية",
  paySiTotal: "التأمينات (الحصتان)",
  payEmployerCost: "تكلفة صاحب العمل",
  payPayDate: "تاريخ الصرف",
  payStatus_draft: "مسودة",
  payStatus_calculated: "محسوب",
  payStatus_approved: "معتمد",
  payStatus_paid: "مدفوع",
  payStale: "تغيرت البنود بعد آخر حساب. أعد الحساب قبل الاعتماد.",
  payApprovals: "الاعتمادات: {n} من {m}",
  payCalculate: "احسب",
  payRecalculate: "أعد الحساب",
  payCalculated: "تم الحساب.",
  payMarkPaid: "تسجيل كمدفوع",
  payMarkedPaid: "تم التسجيل كمدفوع.",
  payReopen: "إعادة فتح",
  payDelete: "حذف هذا المسير",
  payDeleteConfirm: "حذف هذا المسير وبنوده؟",
  payAdjustments: "بنود لمرة واحدة هذا الشهر",
  payRemove: "حذف",
  payPayslips: "قسائم الرواتب",
  payExportSummary: "كشف الرواتب (CSV)",
  payExportBank: "ملف التحويل البنكي (CSV)",
  payExportInsurance: "كشف التأمينات (CSV)",
  payNoPaySet: "لم يُحدد راتب لـ {n} موظف",
  payUnpaidDays: "أيام بدون أجر",
  payPartMonth: "جزء من الشهر",
  payPayslip: "القسيمة",
  payRulesNote: "محسوب بالقواعد المصرية: التأمينات 11% / 18.75% في حدود الأجر التأميني للسنة، وضريبة كسب العمل وفق القانون 7 لسنة 2024 بعد الإعفاء السنوي 20,000، وصندوق الشهداء 0.05%. اطلب من المحاسب مراجعة أول مسير.",
  payAddAdjustment: "إضافة مكافأة أو عمولة أو خصم",
  payElement: "بند الراتب",
  payAmount: "المبلغ (جنيه)",
  payAdd: "إضافة",
  payEarning: "استحقاق",
  payDeduction: "استقطاع",
  payAdjHelp: "الراتب الشهري يأتي من إعداد الرواتب؛ أضف البنود لمرة واحدة هنا ثم أعد الحساب.",
  payBasic: "الراتب الأساسي",
  payAllowances: "البدلات",
  payInsured: "مؤمَّن عليه",
  payNotSet: "غير محدد",
  payView: "عرض",
  payFrom: "ساري من",
  payInsuredLong: "مشمول بالتأمينات الاجتماعية",
  payTaxExempt: "معفى من ضريبة كسب العمل",
  payInsuranceNo: "الرقم التأميني",
  payInsuranceSalary: "الأجر التأميني (اختياري)",
  payAuto: "يُحسب من الراتب",
  payMethod: "طريقة الصرف",
  payBank: "تحويل بنكي",
  payCash: "نقدي",
  payWallet: "محفظة إلكترونية",
  payBankName: "البنك",
  payAccount: "رقم الحساب أو IBAN",
  payHistory: "سجل الراتب",
  payKind: "النوع",
  payTaxable: "خاضع للضريبة",
  payInsurable: "خاضع للتأمينات",
  payMonthly: "شهري",
  payOneOff: "لمرة واحدة",
  payElementsHelp: "البنود الشهرية تُحدد لكل موظف في إعداد الرواتب. البنود لمرة واحدة تُضاف لمسير الشهر.",
  payNewElement: "بند راتب جديد",
  payApprovalLevels: "الاعتماد",
  payOneApprover: "يعتمد شخص واحد",
  payTwoApprovers: "يعتمد شخصان مختلفان",
  payPayDay: "يوم الصرف المعتاد (1–28)",
  payUhi: "خصم التأمين الصحي الشامل",
  payUhiHelp: "فعّل هذا فقط إذا بدأ نظام التأمين الصحي الشامل في محافظتك (1% الموظف، 3% صاحب العمل).",
  payNoMyPayslips: "لا توجد قسائم بعد. تظهر هنا بعد اعتماد الرواتب.",
  navOvertime: "الورديات والعمل الإضافي",
  navLoans: "السلف والقروض",
  otTab: "العمل الإضافي",
  shiftsTab: "الورديات",
  otFind: "استخراج العمل الإضافي من الحضور",
  otFound: "تم العثور على {n} ساعات عمل إضافي.",
  otHelp: "العمل الإضافي هو الوقت بعد نهاية الوردية في أيام العمل (نهاري، وليلي بعد العاشرة مساءً)، وكل وقت العمل في أيام الراحة والعطلات الرسمية. يُصرف المعتمد في مسير الرواتب التالي.",
  otApprovedHours: "ساعات معتمدة",
  ot_day: "إضافي نهاري",
  ot_night: "إضافي ليلي",
  ot_rest_day: "يوم راحة",
  ot_holiday: "عطلة رسمية",
  otStatus_suggested: "للمراجعة",
  otStatus_approved: "معتمد",
  otStatus_rejected: "مرفوض",
  otPaid: "صُرف في الرواتب",
  otSelectAll: "تحديد الكل",
  otSelect: "تحديد",
  otApprovedN: "تم اعتمادها.",
  otRejectedN: "تم رفضها.",
  otNone: "لا يوجد عمل إضافي هنا.",
  otKind: "النوع",
  otHours: "الساعات",
  otAdd: "تسجيل عمل إضافي",
  otAdded: "تم تسجيل العمل الإضافي واعتماده.",
  otSent: "أُرسل العمل الإضافي للاعتماد.",
  otRatesNote: "يُصرف بأجر الساعة (الراتب الشهري ÷ 30 ÷ 8): الإضافي النهاري +35%، والليلي +70%، وأيام الراحة والعطلات بالزيادة القانونية.",
  shiftNone: "لا توجد ورديات بعد. يعمل الجميع بمواعيد الشركة في الإعدادات.",
  shiftBreak: "الاستراحة",
  shiftHelp: "التأخير والعمل الإضافي يتبعان وردية كل شخص؛ ومن ليس له وردية يتبع مواعيد الشركة.",
  shiftAssign: "تعيين الموظفين على وردية",
  shiftOne: "الوردية",
  shiftCompanyHours: "مواعيد الشركة",
  shiftAssignBtn: "تعيين",
  shiftAssigned: "تم تحديث {n} موظف.",
  shiftNew: "وردية جديدة",
  shiftStart: "تبدأ",
  shiftEnd: "تنتهي",
  apprOvertime: "عمل إضافي للاعتماد",
  apprApproveAll: "اعتماد الكل",
  apprOpenOvertime: "فتح العمل الإضافي",
  apprMoreOvertime: "{n} أخرى في صفحة العمل الإضافي.",
  apprOpenLoans: "فتح السلف",
  loanActive: "السلف والقروض الجارية",
  loanOutstanding: "المتبقي للاسترداد",
  loanRequests: "طلبات السلف والقروض",
  loanMonths: "شهر",
  loanFirstMonth: "أول خصم",
  loanInstallments: "عدد الأقساط الشهرية",
  loanApproved: "تمت الموافقة؛ تبدأ الأقساط في الشهر المختار.",
  loanRejected: "تم رفض الطلب.",
  loanNone: "لا توجد سلف أو قروض جارية.",
  loanRecovered: "المسترد",
  loanLeft: "المتبقي",
  loanNext: "القادم",
  loanDone: "كل الأقساط مجدولة",
  loanLeaver: "ترك العمل",
  loanClose: "إغلاق",
  loanCloseAsk: "إغلاق هذا القرض وحذف الأقساط التي لم تُخصم بعد؟ أضف ملاحظة (مثل: سُدد نقدًا).",
  loanClosed: "تم إغلاق القرض.",
  loanRule: "السلف والقروض بدون فوائد، ولا يجوز خصم أكثر من 10% من الأجر الشهري كل شهر (قانون العمل 14 لسنة 2025، المادة 113).",
  loanPast: "المرفوضة والمغلقة",
  loanNew: "منح سلفة أو قرض",
  loanGiven: "تم إنشاء القرض وجدولته.",
  loanGive: "إنشاء",
  loanEach: "قيمة القسط",
  loanKind_loan: "قرض",
  loanKind_advance: "سلفة على الراتب",
  loanStatus_requested: "قيد الانتظار",
  loanStatus_active: "جاري",
  loanStatus_rejected: "مرفوض",
  loanStatus_cancelled: "مغلق",
  myLoans: "سلفي وقروضي",
  loanAsk: "طلب سلفة أو قرض",
  loanReason: "السبب",
  loanNoneMine: "لا توجد سلف أو قروض.",
  apprExpenses: "طلبات المصروفات",
  apprOpenExpenses: "فتح المصروفات",
  asCondition: "الحالة",
  asDamaged: "تالفة",
  asGood: "سليمة",
  asHandOut: "تسليم",
  asHolder: "في عهدة",
  asIssued: "تم التسليم.",
  asItem: "البند",
  asLeavers: "{n} عُهد ما زالت مع موظفين غادروا",
  asLost: "مفقودة",
  asNew: "إضافة عهدة",
  asNone: "لا توجد عُهد هنا.",
  asReturned: "تم الاستلام.",
  asSerial: "الرقم التسلسلي",
  asTag: "الكود",
  asTakeBack: "استلام",
  asValue: "القيمة (جنيه)",
  asStatus_available: "متاحة",
  asStatus_assigned: "مع موظف",
  asStatus_repair: "في الصيانة",
  asStatus_retired: "مستبعدة",
  asStatus_lost: "مفقودة",
  asCat_laptop: "لابتوب",
  asCat_phone: "هاتف",
  asCat_sim: "شريحة",
  asCat_tablet: "تابلت",
  asCat_vehicle: "سيارة",
  asCat_card: "كارت",
  asCat_key: "مفتاح",
  asCat_uniform: "زي",
  asCat_tools: "أدوات",
  asCat_other: "أخرى",
  bnCompanyPays: "تدفع الشركة",
  bnDepFactor: "كل تابع يضيف (× التكلفة)",
  bnDependents: "تابعين",
  bnEmployeeMonthly: "حصة الموظفين شهريًا",
  bnEmployeePays: "يدفع الموظف",
  bnEmployerMonthly: "تكلفة الشركة شهريًا",
  bnEnd: "إنهاء",
  bnEnded: "تم الإنهاء.",
  bnEnroll: "اشتراك",
  bnEnrolled: "تم الاشتراك.",
  bnHelp: "تُخصم حصة الموظف في الرواتب كل شهر؛ والتابعون يزيدون الحصتين.",
  bnMonth: "شهر",
  bnNew: "ميزة جديدة",
  bnNone: "لا توجد مزايا بعد.",
  bnProvider: "مقدم الخدمة",
  bnCat_medical: "تأمين طبي",
  bnCat_life: "تأمين على الحياة",
  bnCat_transport: "انتقالات",
  bnCat_phone: "هاتف",
  bnCat_meal: "وجبات",
  bnCat_wellbeing: "رفاهية",
  bnCat_other: "أخرى",
  exClaim: "طلب استرداد مصروف",
  exHelp: "يعتمد المدير أولًا ثم الإدارة المالية. تُرد المبالغ المعتمدة في مسير الرواتب التالي.",
  exHelpFin: "اعتمادك نهائي. تُرد المبالغ المعتمدة معفاة من الضريبة في الرواتب التالية.",
  exNone: "لا توجد طلبات هنا.",
  exNoneMine: "لا توجد طلبات مصروفات.",
  exPaid: "تم الرد",
  exReceipt: "رقم الإيصال",
  exReceiptHint: "مثل رقم الفاتورة أو الإيصال",
  exRefunded: "تم ردها في الرواتب",
  exSent: "تم إرسال الطلب.",
  exSubmit: "إرسال",
  exToRefund: "معتمدة للرد",
  exWaiting: "بانتظار الاعتماد",
  exWaitingShort: "بانتظار",
  exWhat: "الغرض",
  exStatus_submitted: "بانتظار المدير",
  exStatus_manager_approved: "بانتظار المالية",
  exStatus_approved: "معتمد",
  exStatus_rejected: "مرفوض",
  exStatus_cancelled: "ملغي",
  exCat_travel: "سفر",
  exCat_transport: "انتقالات",
  exCat_meals: "وجبات",
  exCat_accommodation: "إقامة",
  exCat_supplies: "مستلزمات",
  exCat_phone: "هاتف",
  exCat_training: "تدريب",
  exCat_other: "أخرى",
  myAssets: "عُهد الشركة معي",
  myAssetsHelp: "سلّمها للموارد البشرية في آخر يوم عمل.",
  myExpenses: "مصروفاتي",
  navAssets: "العُهد",
  navExpenses: "المصروفات",
  navOffboarding: "إنهاء الخدمة",
  navRewards: "المكافآت والمزايا",
  rwApprove: "اعتماد وإضافة للرواتب",
  rwApproved: "تم الاعتماد؛ المبالغ في رواتب ذلك الشهر.",
  rwCreate: "إنشاء",
  rwDeleteAsk: "حذف هذه الخطة المسودة؟",
  rwEveryone: "الجميع",
  rwFill: "تعبئة",
  rwFillHow: "طريقة التعبئة",
  rwFilled: "تمت التعبئة.",
  rwFixed: "مبلغ ثابت",
  rwHelp: "جهّز مكافأة أو عمولة أو جائزة أو حصة أرباح لشهر، وراجع المبالغ، ثم اعتمدها لتُضاف لرواتب ذلك الشهر.",
  rwMonthsOfBasic: "عدد أشهر من الأساسي",
  rwNew: "خطة جديدة",
  rwNone: "لا توجد خطط بعد.",
  rwPayMonth: "تُصرف في",
  rwPlans: "خطط المكافآت والأرباح",
  rwProfitNote: "حصة الأرباح مضبوطة كغير خاضعة للضريبة والتأمينات افتراضيًا؛ تأكد مع المحاسب (بنود الراتب).",
  rwTabBenefits: "المزايا",
  rwTabPlans: "المكافآت والأرباح",
  rwTitle: "العنوان",
  rwTitleExample: "مثال: مكافأة نهاية العام 2026",
  rwTotal: "الإجمالي",
  rwKey_bonus: "مكافأة",
  rwKey_commission: "عمولة",
  rwKey_award: "جائزة",
  rwKey_profit_share: "حصة أرباح",
  rwStatus_draft: "مسودة",
  rwStatus_approved: "في الرواتب",
  rwStatus_paid: "مدفوعة",
  xCalculate: "حساب المستحقات",
  xCalculated: "تم حساب المستحقات؛ راجع المبالغ وعدّلها.",
  xChecklist: "قائمة المهام",
  xClose: "إغلاق ملف الخروج",
  xClosed: "تم الإغلاق.",
  xComments: "ملاحظات",
  xCourtMin: "عند الفصل بدون سبب مشروع تقضي المحاكم بأجر شهرين على الأقل عن كل سنة ({amt}).",
  xDone: "المغلقة والمسحوبة",
  xInPayroll: "المستحقات في رواتب {m}.",
  xInProgress: "حالات خروج جارية",
  xInfo: "الراتب الشهري {pay} · الأجر اليومي {daily} · {years} سنة خدمة.",
  xInterview: "مقابلة نهاية الخدمة",
  xLastDay: "آخر يوم عمل",
  xLeaveDays: "أيام الإجازة المتبقية",
  xMainReason: "السبب الرئيسي للمغادرة",
  xNet: "إجمالي المستحقات",
  xNone: "لا يوجد من يغادر حاليًا.",
  xNoticeDate: "تاريخ الإخطار",
  xNoticeMonths: "بدل الإخطار (أشهر)",
  xRecommend: "ينصح بالعمل لدينا",
  xResign: "تقديم استقالة",
  xResignHelp: "تُرسل استقالتك للموارد البشرية. يطلب القانون إخطارًا كتابيًا قبل ثلاثة أشهر.",
  xResignOpen: "إجراءات خروجك جارية؛ آخر يوم {d}.",
  xResignSend: "إرسال الاستقالة",
  xResignSent: "تم إرسال استقالتك للموارد البشرية.",
  xResignTitle: "الاستقالة",
  xRules: "قانون العمل 14 لسنة 2025: إخطار كتابي ثلاثة أشهر؛ صرف كل المستحقات خلال 7 أيام عمل من آخر يوم؛ ومكافأة نهاية خدمة لغير المؤمن عليهم فقط.",
  xSettle: "إنهاء الخدمة وإرسال للرواتب",
  xSettleAsk: "إنهاء الخدمة في آخر يوم وإضافة المستحقات لرواتب ذلك الشهر؟",
  xSettled: "تم: المستحقات في الرواتب.",
  xSettlement: "المستحقات النهائية",
  xSettlementHelp: "راتب أيام العمل في الشهر الأخير يُصرف في رواتب ذلك الشهر. تُخصم القروض المتبقية والعُهد غير المسلمة من المستحقات.",
  xStart: "بدء إجراءات خروج",
  xStartBtn: "بدء",
  xStillHolds: "ما زال معه عُهد",
  xUninsured: "غير مؤمن عليه: تُستحق مكافأة نهاية خدمة (م. 172).",
  xWithdraw: "سحب",
  xWithdrawAsk: "سحب إجراءات الخروج (مثلًا تراجع عن الاستقالة)؟",
  xWithdrawn: "تم السحب.",
  xLeaveAmount: "مقابل رصيد الإجازات",
  xNoticePay: "بدل مهلة الإخطار",
  xGratuity: "مكافأة نهاية الخدمة",
  xCompensation: "تعويض متفق عليه",
  xLoan: "رصيد القرض",
  xAssets: "عُهد لم تُسلّم",
  xType_resignation: "استقالة",
  xType_termination: "إنهاء من الشركة",
  xType_contract_end: "انتهاء العقد",
  xType_retirement: "تقاعد",
  xType_mutual: "اتفاق متبادل",
  xType_other: "أخرى",
  xStatus_open: "جارية",
  xStatus_settled: "تمت التسوية",
  xStatus_closed: "مغلقة",
  xStatus_cancelled: "مسحوبة",
  xOwner_hr: "الموارد البشرية",
  xOwner_manager: "المدير",
  xOwner_it: "تقنية المعلومات",
  xOwner_finance: "المالية",
  xOwner_employee: "الموظف",
  xR_career: "فرصة مهنية",
  xR_pay: "الراتب والمزايا",
  xR_manager: "المدير",
  xR_workload: "ضغط العمل",
  xR_relocation: "انتقال",
  xR_study: "دراسة",
  xR_family: "أسباب عائلية",
  xR_other: "أخرى",
  x_rating_manager: "المدير",
  x_rating_pay: "الراتب",
  x_rating_growth: "فرص النمو",
  navOnboarding: "التهيئة",
  navRecruitment: "التوظيف",
  obAddStep: "إضافة خطوة",
  obDays: "الأيام من تاريخ البدء",
  obDueAfter: "بعد {n} يوم من البدء",
  obDueBefore: "قبل البدء بـ {n} يوم",
  obDueFirstDay: "في اليوم الأول",
  obDueProbation: "قبل نهاية الاختبار بـ {n} يوم",
  obFromProbation: "الحساب من نهاية فترة الاختبار",
  obMine: "البداية معنا",
  obNewJoiners: "الموظفون الجدد",
  obNone: "لا توجد تهيئة جارية.",
  obNotStarted: "موظفون جدد بدون تهيئة",
  obOpenProfile: "فتح الملف",
  obOverdue: "متأخرة",
  obOwner: "المسؤول",
  obPeople: "الموظفون",
  obStart: "بدء التهيئة",
  obStarted: "بدأت التهيئة.",
  obStarts: "يبدأ",
  obTemplate: "نموذج قائمة المهام",
  obTemplateHelp: "يحصل المعينون الجدد على هذه الخطوات بمواعيد محسوبة من تاريخ البدء (أو نهاية الاختبار). التعديلات تسري على من يبدأ التهيئة لاحقًا.",
  rqActive: "الوظائف المفتوحة",
  rqAddCandidate: "إضافة مرشح",
  rqApproveOpen: "اعتماد وفتح",
  rqAsk: "طلب تعيين جديد",
  rqAtInterview: "في المقابلات",
  rqAtOffer: "لديهم عرض",
  rqCancel: "إلغاء الوظيفة",
  rqClosed: "المكتملة والملغاة",
  rqCurrentTitle: "الوظيفة الحالية",
  rqCv: "السيرة الذاتية",
  rqCvLink: "رابط السيرة الذاتية",
  rqDaysToHire: "متوسط {n} يوم للتعيين",
  rqDeclined: "رفض العرض",
  rqDescription: "وصف الوظيفة",
  rqHeadcount: "العدد",
  rqHire: "تعيين",
  rqHireAsk: "إنشاء سجل الموظف من هذا المرشح والعرض؟",
  rqHireHelp: "التعيين ينشئ الموظف بالوظيفة والإدارة والمدير والراتب من العرض وثلاثة أشهر اختبار، ويبدأ التهيئة.",
  rqHired: "تم التعيين",
  rqHiredMsg: "تم التعيين: سجل الموظف والتهيئة جاهزان.",
  rqHires12: "التعيينات في 12 شهرًا",
  rqHold: "إيقاف مؤقت",
  rqInPipeline: "في مسار التوظيف",
  rqInterviewer: "المُقابِل",
  rqInterviews: "المقابلات",
  rqMoveTo: "المرحلة",
  rqNew: "فتح وظيفة",
  rqNewRole: "وظيفة جديدة",
  rqNoInterviews: "لا توجد مقابلات بعد.",
  rqNone: "لا توجد وظائف مفتوحة.",
  rqOffer: "العرض الوظيفي",
  rqOfferSent: "تم حفظ العرض كمرسل.",
  rqOpen: "فتح الوظيفة",
  rqOpenEmployee: "فتح الموظف",
  rqOpenPositions: "شواغر مفتوحة",
  rqOpened: "تم فتح الوظيفة.",
  rqOut: "المرفوضون والمنسحبون",
  rqRating: "التقييم",
  rqReason: "السبب",
  rqRecommend: "التوصية",
  rqRejectReason: "السبب (اختياري)",
  rqRejected: "تم رفض المرشح.",
  rqReopen: "إعادة فتح",
  rqReplacement: "بديل",
  rqRole: "الوظيفة",
  rqRoles: "وظائف",
  rqScore: "الدرجة",
  rqSendOffer: "إرسال العرض",
  rqSource: "المصدر",
  rqStart: "تاريخ البدء",
  rqUpcoming: "قادمة",
  rqUpdateOffer: "تحديث العرض",
  rqWaitingApproval: "وظائف بانتظار الاعتماد",
  rqWithdrawn: "انسحب",
  stage_applied: "تقدم",
  stage_screening: "فرز",
  stage_interview: "مقابلة",
  stage_offer: "عرض",
  stage_hired: "تم التعيين",
  stage_rejected: "مرفوض",
  stage_withdrawn: "انسحب",
  rqStatus_requested: "بانتظار الموارد البشرية",
  rqStatus_open: "مفتوحة",
  rqStatus_on_hold: "متوقفة",
  rqStatus_filled: "مكتملة",
  rqStatus_cancelled: "ملغاة",
  ivKind_phone: "مكالمة",
  ivKind_video: "مكالمة فيديو",
  ivKind_onsite: "حضوري",
  ivKind_technical: "فنية",
  ivKind_hr: "موارد بشرية",
  ivRec_hire: "يُعيَّن",
  ivRec_maybe: "ربما",
  ivRec_no: "لا",
  ofStatus_draft: "مسودة",
  ofStatus_sent: "مُرسل",
  ofStatus_accepted: "مقبول",
  ofStatus_declined: "مرفوض",
  navPerformance: "الأداء",
  navTraining: "التدريب",
  pfReviews: "التقييمات",
  pfGoals: "الأهداف",
  pfAckComment: "تعليقك (اختياري)",
  pfAcknowledge: "إقرار بالاطلاع",
  pfAddGoal: "إضافة هدف",
  pfAddMissing: "إضافة المنضمين الجدد",
  pfAddMyGoal: "أضف هدفًا خاصًا بك",
  pfAverage: "متوسط التقييم",
  pfClose: "إغلاق الدورة",
  pfClosed: "تم إغلاق الدورة.",
  pfComments: "التعليق العام",
  pfCycle: "دورة التقييم",
  pfDistribution: "توزيع التقييمات",
  pfDue: "الموعد",
  pfEmployeeSaid: "تعليق الموظف",
  pfEndHelp: "عدم التثبيت يعني إنهاء الخدمة قبل نهاية الاختبار؛ ابدأ إجراءات الخروج من إنهاء الخدمة (فترة الاختبار مرة واحدة وبحد أقصى 3 أشهر وفق قانون العمل).",
  pfGoal: "الهدف",
  pfImprovements: "مجالات التحسين",
  pfLaunch: "إطلاق التقييمات",
  pfLaunched: "تم إنشاء التقييمات.",
  pfManager: "المدير",
  pfManagerDone: "تقييمات المديرين المكتملة",
  pfManagerDue: "موعد تقييمات المديرين",
  pfMine: "أدائي",
  pfMyComments: "كيف كان أداؤك؟",
  pfMyGoals: "أهدافي",
  pfMyRating: "تقييمي لنفسي",
  pfNewCycle: "دورة تقييم جديدة",
  pfNoGoals: "لا توجد أهداف بعد.",
  pfNoReviews: "لا توجد تقييمات في هذه الدورة بعد. أطلقها لإنشائها.",
  pfNoSelfYet: "لم يصل التقييم الذاتي بعد؛ يمكنك كتابة تقييمك.",
  pfOutcome: "قرار فترة الاختبار",
  pfOutcome_confirm: "تثبيت",
  pfOutcome_end: "عدم التثبيت",
  pfProbationHelp: "تشمل كل من تنتهي فترة اختباره بين هذين التاريخين.",
  pfProgress: "نسبة الإنجاز",
  pfRating: "التقييم",
  pfReviewer: "المُقيِّم",
  pfSelf: "ذاتي",
  pfSelfDone: "التقييمات الذاتية المكتملة",
  pfSelfDue: "موعد التقييم الذاتي",
  pfStrengths: "نقاط القوة",
  pfSubmit: "إرسال التقييم",
  pfSubmitSelf: "إرسال تقييمي الذاتي",
  pfWeight: "الوزن %",
  pfStatus_draft: "مسودة",
  pfStatus_active: "جارية",
  pfStatus_closed: "مغلقة",
  pfKind_annual: "سنوي",
  pfKind_mid_year: "نصف سنوي",
  pfKind_quarterly: "ربع سنوي",
  pfKind_probation: "فترة الاختبار",
  pfR_self: "بانتظار التقييم الذاتي",
  pfR_manager: "بانتظار المدير",
  pfR_completed: "مكتمل",
  pfR_acknowledged: "تم الإقرار",
  pfScale_5: "متميز",
  pfScale_4: "يفوق التوقعات",
  pfScale_3: "يلبي التوقعات",
  pfScale_2: "يلبي جزئيًا",
  pfScale_1: "لا يلبي",
  gStatus_active: "جاري",
  gStatus_achieved: "تحقق",
  gStatus_missed: "لم يتحقق",
  gStatus_dropped: "ملغي",
  trAskFor: "اطلب دورة…",
  trCancelSession: "إلغاء الجلسة",
  trCapacity: "المقاعد",
  trCatalogue: "دليل الدورات",
  trCompletions: "إتمام",
  trCost: "التكلفة للفرد (جنيه)",
  trCourse: "الدورة",
  trEnroll: "تسجيل",
  trEnrollMore: "تسجيل آخرين",
  trEnrolled: "تم التسجيل.",
  trExpired: "منتهية",
  trExpires: "تنتهي",
  trExpiring: "شهادات تنتهي خلال 60 يومًا",
  trExpiringTitle: "شهادات للتجديد",
  trHours: "الساعات",
  trHoursYear: "ساعات التدريب هذا العام",
  trMandatory: "إلزامية",
  trMarkDone: "تسجيل الإتمام",
  trMine: "تدريبي",
  trMissing: "ما زال مطلوبًا",
  trNewCourse: "دورة جديدة",
  trNewSession: "جدولة جلسة",
  trNoSessions: "لا توجد جلسات بعد.",
  trNoShow: "لم يحضر",
  trNoneMine: "لا يوجد تدريب بعد.",
  trRecorded: "تم التسجيل.",
  trRequests: "طلبات التدريب",
  trScore: "الدرجة",
  trSessionAdded: "تمت جدولة الجلسة.",
  trSessionDone: "تسجيل انتهاء الجلسة",
  trSessions: "الجلسات",
  trSpendYear: "الإنفاق هذا العام",
  trTrainer: "المدرب",
  trValid: "الشهادة سارية {n} شهرًا",
  trValidity: "صلاحية الشهادة (أشهر)",
  trWhere: "المكان",
  trS_planned: "مجدولة",
  trS_done: "تمت",
  trS_cancelled: "ملغاة",
  trR_requested: "مطلوب",
  trR_enrolled: "مسجل",
  trR_completed: "أتم",
  trR_no_show: "لم يحضر",
  trR_declined: "مرفوض",
  trCat_induction: "تعريفية",
  trCat_compliance: "امتثال",
  trCat_safety: "سلامة",
  trCat_skills: "مهارات",
  trCat_technical: "فنية",
  trCat_leadership: "قيادة",
  trCat_language: "لغات",
  trCat_other: "أخرى",
  apprReviews: "تقييمات مطلوبة منك",
  apprOpenPerformance: "فتح الأداء",
  apprOpenTraining: "فتح التدريب",
  navRelations: "علاقات العمل",
  navEngagement: "التفاعل",
  navAnalytics: "تحليلات الموظفين",
  delete: "حذف",
  seeAll: "عرض الكل",
  erOpen: "حالات مفتوحة",
  erGrievancesOpen: "تظلمات مفتوحة",
  erNone: "لا توجد حالات.",
  erCase: "الحالة",
  erConfidential: "سري",
  erConfidentialLong: "سري: يراه قسم الموارد البشرية فقط وليس المدير",
  erNew: "فتح حالة",
  erCategory: "الفئة",
  erIncident: "تاريخ الواقعة",
  erWhatHappened: "ما الذي حدث",
  erOpenBtn: "فتح الحالة",
  erRules: "قانون العمل المصري 14/2025: يجب إخطار العامل كتابة وسماع أقواله قبل توقيع أي جزاء، ولا يزيد الخصم على أجر 5 أيام أساسي عن المخالفة الواحدة وفي الشهر.",
  erHeard: "تم سماع الأقوال",
  erDays: "أيام",
  erInPayroll: "تُخصم في الرواتب",
  erTimeline: "السجل",
  erAddNote: "إضافة إلى السجل",
  erDecide: "القرار",
  erOutcome: "النتيجة",
  erDaysBasic: "أيام من الأجر الأساسي (حد أقصى 5)",
  erHearFirst: "سجّل محضر سماع الأقوال قبل توقيع الجزاء.",
  erDecideBtn: "تسجيل القرار",
  erDecided: "تم تسجيل القرار ويمكن للموظف رؤيته.",
  erClose: "إغلاق الحالة",
  erClosedMsg: "تم إغلاق الحالة.",
  erMine: "التظلمات والحالات",
  erRaise: "تقديم تظلم",
  erRaiseHelp: "يطلع عليه قسم الموارد البشرية فقط، ولا يُبلَّغ مديرك إلا إذا احتاج القسم لإشراكه.",
  erRaised: "تم الإرسال إلى الموارد البشرية بسرية.",
  erKind_disciplinary: "تأديبي",
  erKind_grievance: "تظلم",
  erStatus_open: "مفتوحة",
  erStatus_investigating: "قيد التحقيق",
  erStatus_decided: "صدر القرار",
  erStatus_closed: "مغلقة",
  erCat_attendance: "الحضور",
  erCat_conduct: "السلوك",
  erCat_performance: "الأداء",
  erCat_safety: "السلامة",
  erCat_policy: "مخالفة لائحة",
  erCat_harassment: "تحرش أو مضايقة",
  erCat_pay: "الأجر",
  erCat_workload: "عبء العمل",
  erCat_manager: "المدير",
  erCat_other: "أخرى",
  erOut_no_action: "لا إجراء",
  erOut_verbal_warning: "لفت نظر شفهي",
  erOut_written_warning: "إنذار كتابي",
  erOut_deduction: "خصم من الأجر",
  erOut_suspension: "إيقاف عن العمل",
  erOut_final_warning: "إنذار نهائي",
  erOut_dismissal_referral: "إحالة للفصل",
  erOut_resolved: "تمت التسوية",
  erOut_partly_upheld: "قُبل جزئيًا",
  erOut_not_upheld: "لم يُقبل",
  erNote_note: "ملاحظة",
  erNote_notice: "إخطار كتابي",
  erNote_hearing: "سماع أقوال",
  erNote_evidence: "دليل",
  erNote_decision: "قرار",
  erNote_status: "الحالة",
  engTab_news: "الإعلانات",
  engTab_surveys: "الاستبيانات",
  engTab_kudos: "التقدير",
  annNew: "إعلان جديد",
  annEdit: "تعديل الإعلان",
  annBody: "النص",
  annExpires: "يظهر حتى (اختياري)",
  annPinned: "مثبت",
  annPublish: "نشر",
  annNone: "لا توجد إعلانات بعد.",
  annNewBadge: "جديد",
  annUntil: "حتى",
  annMarkRead: "تعليم كمقروء",
  annDeleteConfirm: "حذف هذا الإعلان؟",
  annHome: "أخبار الشركة",
  svForYou: "استبيانات لك",
  svNoneOpen: "لا يوجد ما تجيب عنه الآن.",
  svCloses: "يغلق في",
  svAnonymous: "مجهول الهوية",
  svAnswer: "أجب",
  svAnsweredN: "أجبت عن {n} استبيان. شكرًا لك.",
  svThanks: "شكرًا لك، تم حفظ إجاباتك.",
  svManage: "إدارة الاستبيانات",
  svNew: "استبيان جديد",
  svQuestions: "الأسئلة",
  svLaunch: "إطلاق",
  svLaunched: "الاستبيان متاح الآن للجميع.",
  svCloseBtn: "إغلاق",
  svResults: "النتائج",
  svStatus_draft: "مسودة",
  svStatus_open: "مفتوح",
  svStatus_closed: "مغلق",
  svAnonNote: "مجهول الهوية: تُحفظ إجاباتك بدون اسمك، ولا تظهر النتائج إلا بعد 3 إجابات على الأقل.",
  svNamedNote: "هذا الاستبيان غير مجهول الهوية؛ يمكن للموارد البشرية معرفة من أجاب.",
  svMissing: "يرجى الإجابة عن",
  svScaleEnds: "1 = أرفض بشدة · 5 = أوافق بشدة",
  svEnpsEnds: "0 = غير محتمل إطلاقًا · 10 = محتمل جدًا",
  svSubmit: "إرسال الإجابات",
  svIntro: "مقدمة",
  svAnonLong: "مجهول الهوية (لا يمكن تغييره بعد الحفظ)",
  svQuestion: "السؤال",
  svOptions: "الخيارات، مفصولة بفواصل",
  svKind_scale: "مقياس 1–5",
  svKind_enps: "eNPS ‏0–10",
  svKind_choice: "اختيار",
  svKind_text: "تعليق",
  svRate: "أجاب {n} من {m} شخص.",
  svHidden: "تظهر النتائج بعد 3 إجابات على الأقل حفاظًا على السرية.",
  svEnpsHelp: "نسبة المروّجين (9–10) ناقص نسبة المنتقدين (0–6)",
  svByDept: "حسب القسم",
  kdGive: "قدّر زميلًا",
  kdTo: "الزميل",
  kdValue: "عن",
  kdMessage: "الرسالة",
  kdSend: "إرسال",
  kdSent: "تم الإرسال ويظهر للجميع في الصفحة.",
  kdFeed: "لوحة التقدير",
  kdNone: "لا يوجد تقدير بعد. كن الأول!",
  kdV_teamwork: "روح الفريق",
  kdV_customer: "خدمة العملاء",
  kdV_ownership: "تحمل المسؤولية",
  kdV_innovation: "الابتكار",
  kdV_excellence: "التميز",
  kdV_helpfulness: "المساعدة",
  anHeadcount: "عدد الموظفين في نهاية الفترة",
  anHires: "التعيينات",
  anDaysToHire: "متوسط أيام التعيين",
  anApplications: "الطلبات",
  anOfferAccept: "قبول العروض",
  anAvgRating: "متوسط التقييم",
  anGoals: "الأهداف المحققة",
  anTrainingHours: "ساعات التدريب",
  anPerHead: "لكل موظف",
  anTrainingSpend: "تكلفة التدريب",
  anCompletions: "إتمام الدورات",
  anNoShows: "الغياب عن الدورات",
  anExpiring: "شهادات تنتهي (60 يومًا)",
  anEnps: "eNPS",
  anParticipation: "المشاركة في الاستبيان",
  anKudos: "مرات التقدير",
  anDisciplinary: "حالات تأديبية",
  anGrievances: "التظلمات",
  anOpenCases: "مفتوحة الآن",
  anAppsBySource: "الطلبات حسب المصدر",
  anRatingSpread: "توزيع التقييمات (آخر دورة)",
  anRatingByDept: "متوسط التقييم حسب القسم",
  anKudosByValue: "التقدير حسب القيمة",
  anCaseOutcomes: "نتائج الحالات",
  anNote: "يعرض الأداء آخر دورة تقييم مكتملة، والتفاعل آخر استبيان به سؤال eNPS، وباقي الأرقام حسب الفترة المختارة.",
  fileAttach: "إرفاق ملف",
  fileReplace: "استبدال",
  fileUploading: "جارٍ الرفع…",
  fileRemoveConfirm: "حذف هذا الملف؟",
  fileLabel: "ملف (PDF أو صورة أو Word أو Excel، حتى 10 ميجابايت)",
  fileFailedLater: "تعذّر رفع الملف؛ أرفقه من القائمة.",
  rqCvFile: "السيرة الذاتية",
  impStep1: "حمّل نموذج Excel واملأ موظفًا في كل صف في ورقة Employees؛ ورقة Guide تشرح كل عمود. المطلوب فقط: الاسم الأول واسم العائلة وتاريخ التعيين.",
  impTemplate: "تحميل نموذج Excel",
  impStep2: "اختر الملف بعد ملئه (‎.xlsx أو ‎.csv). يفحص PeopleCore كل صف أولًا ولا يُحفظ شيء قبل تأكيدك.",
  impChoose: "اختيار ملف",
  impChecking: "جارٍ فحص الصفوف…",
  impEmpty: "لم يُعثر على صفوف موظفين أسفل صف العناوين.",
  impTooMany: "استورد 2000 صف كحد أقصى في المرة؛ قسّم الملف.",
  impOldXls: "ملفات ‎.xls القديمة غير مدعومة؛ احفظها كـ ‎.xlsx من Excel.",
  impUnknownCols: "هذه الأعمدة غير معروفة وسيتم تجاهلها",
  impReady: "جاهز",
  impWithNotes: "بملاحظات",
  impWillFail: "تحتاج تصحيحًا",
  impFixHint: "سيتم تخطي الصفوف التي تحتاج تصحيحًا. حمّلها وصحّحها ثم استوردها لاحقًا، أو صحّح الملف كله واختره مجددًا.",
  impAllGood: "اجتازت كل الصفوف الفحص.",
  impDone: "تم الاستيراد. الموظفون الجدد في القائمة مع الأجر والتأمين والبنك والمدير ورصيد الإجازة حيثما وُجدت.",
  impProblem: "المشكلة أو الملاحظة",
  impDownloadProblems: "تحميل الصفوف المطلوب تصحيحها (‎.xlsx)",
  impRun: "استيراد {n} موظف",
  roleHelp: "غيّر الدور من القائمة؛ يسري عند فتح الشخص للتطبيق في المرة القادمة. لا يمكنك تغيير دورك، ويجب أن يبقى مسؤول موارد بشرية واحد على الأقل.",
  roleChanged: "أصبح {name} الآن {role}.",
  you: "أنت",
  switchOff: "إيقاف الدخول",
  switchOn: "إعادة الدخول",
  accessOffConfirm: "إيقاف دخول هذا الشخص؟ يبقى ملفه الوظيفي لكنه لن يستطيع الدخول إلى هذه الشركة.",
  accessOff: "تم إيقاف الدخول.",
  accessOn: "تمت إعادة الدخول.",
};

const dicts: Record<Lang, Dict> = { en, ar };

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem("pc.lang");
    return v === "ar" ? "ar" : "en";
  } catch {
    return "en";
  }
}

interface I18n {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: StringKey) => string;
  /** Pick the Arabic or English variant of a database field. */
  pick: (enText: string, arText: string | null | undefined) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    try {
      localStorage.setItem("pc.lang", lang);
    } catch {
      /* storage unavailable: keep the choice for this visit only */
    }
  }, [lang]);
  const value: I18n = {
    lang,
    setLang: setLangState,
    t: (k) => dicts[lang][k],
    pick: (e, a) => (lang === "ar" && a ? a : e),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n must be used inside I18nProvider");
  return v;
}
